/**
 * TriSys Web API Integration
 * 
 * Handles all communication with the TriSys Web API backend.
 * See specifications/TriSys-Web-API-Integration.md for full API documentation.
 */

import { Logger } from '../Logger';
import { oemConfig, SessionStorageType } from '../OEMConfig';

const logger = Logger.scope('TriSysWebAPI');

// Session storage key for the DataServicesKey
const SESSION_KEY = 'trisys_data_services_key';
const POST_LOGIN_CREDS_KEY = 'trisys_post_login_creds';
const STANDING_DATA_KEY = 'trisys_standing_data';
const API_ENDPOINT_KEY = 'trisys_api_endpoint';

// In-memory storage for most secure mode (banking apps)
let memorySessionKey: string | null = null;

// API Configuration - SiteKey can be overridden by tenant's oem.json
const API_CONFIG = {
  baseUrl: 'https://api.trisys.co.uk',
  
  // Dev-02 for debugging Web API locally
  //baseUrl: 'http://54.74.244.253:45455',
  
  defaultSiteKey: '20251219-re90-9e2c-2904-flexiva.ai26'
};

/**
 * Get the SiteKey - tenant-specific from OEM config, or default
 */
export function getSiteKey(): string {
  // Import dynamically to avoid circular dependency
  try {
    const tenantSiteKey = oemConfig.getSiteKey();
    if (tenantSiteKey) {
      return tenantSiteKey;
    }
  } catch {
    // OEM config may not be loaded yet
  }
  return API_CONFIG.defaultSiteKey;
}

// ===== Response Types =====

export interface TriSysAuthResponse {
  DataServicesKey: string | null;
  WebAPIEndPoint: string | null;
  CredentialsCorrectButSomethingElsePreventsLogin: boolean;
  Success: boolean;
  ErrorMessage: string | null;
}

export interface TriSysVerifyResponse {
  Success: boolean;
  ErrorMessage: string | null;
}

export interface TriSysVersionResponse {
  id: number;
  Name: string;
  Version: string;
  Success: boolean;
  ErrorMessage: string | null;
}

export interface TriSysContact {
  ContactId: number;
  FullName: string;
  Forenames: string;
  Surname: string;
  JobTitle: string;
  CompanyName: string;
  CompanyId: number;
  ContactPhoto: string | null;
  ContactPhotoURL: string | null;
  CompanyLogoURL: string | null;
  WorkEMail: string;
  PersonalEMail: string | null;
  MobileTelNo: string | null;
  WorkMobileTelNo: string | null;
  WorkTelNo: string | null;
  LinkedIn: string | null;
  Twitter: string | null;
  Facebook: string | null;
  Gender: string | null;
  DateOfBirth: string | null;
  Title: string | null;
  Status: string | null;
}

export interface TriSysSubscriber {
  AccountMaintainer: boolean;
  UserHasRemoteDesktop: boolean;
  CompanyHasRemoteDesktops: boolean;
  ASPUserType: string;
  ASPdbName: string;
}

export interface TriSysCRMContact {
  ApexDeveloper: boolean;
  SendApexScreenshot: boolean;
  Subscriber: TriSysSubscriber;
  ContactId: number;
  FullName: string;
  JobTitle: string;
  CompanyName: string;
}

export interface TriSysLoggedInUser {
  UserId: number;
  LoginName: string;
  FullName: string;
  ForenameAndSurname: string;
  CompanyName: string | null;
  ContactId: number;
  Contact: TriSysContact;
  CRMContact: TriSysCRMContact;
}

export interface TriSysCredentialsResponse {
  SQLServer: string;
  Database: string;
  LoggedInUser: TriSysLoggedInUser;
  ShowDummyDashboardData: boolean;
  TrialAccount: boolean;
  Success: boolean;
  ErrorMessage: string | null;
}

// ===== User Profile (simplified for UI consumption) =====

export interface UserProfile {
  userId: number;
  contactId: number;
  loginName: string;
  displayName: string;
  email: string;
  jobTitle: string;
  companyName: string;
  avatar: string | null;
  companyLogo: string | null;
  linkedIn: string | null;
  isAdmin: boolean;
  isDeveloper: boolean;
  showDummyData: boolean;
  isTrialAccount: boolean;
}

// ===== Standing Data (cached reference data for lookups and CRUD) =====

export interface StandingDataAddressType {
  AddressTypeId: number;
  Name: string;
  Description: string;
  Ordering: number;
}

export interface StandingDataContactTitle {
  ContactTitleId: number;
  Name: string;
  Description: string;
  Ordering: number;
  Male: number;      // 0=Female, 1=Male, 2=Unknown
  Married: number;   // 0=No, 1=Yes, 2=Unknown
}

export interface StandingDataContactPriority {
  ContactPriorityId: number;
  ContactPriority: string;
  HTMLColour: string;
  Colour: number;
  Ordering: number;
  Description: string;
}

export interface StandingDataContactTypeColour {
  ContactType: string;  // Candidate, Client, ClientCandidate, User
  Colour: number;
}

export interface StandingDataCurrency {
  CurrencyId: number;
  Symbol: string;
  Description: string;
  Ordering: number;
}

export interface StandingDataConsultancyPeriod {
  ConsultancyPeriodId: number;
  Name: string;  // Hour, Day, Week, Month, Annum
}

export interface StandingDataConsultancyPeriodRelationship {
  BaseConsultancyPeriodId: number;
  RelatedConsultancyPeriodId: number;
  Multiplier: number;
}

/**
 * Skill Category - Links a category of skills to a FieldId for dropdown lookups
 * Example: Category="Gender" with FieldId=5938 means skills in this category populate the Gender dropdown
 */
export interface StandingDataSkillCategory {
  SkillCategoryId: number;
  FieldId: number;              // Links to FieldDescriptions.FieldId
  Category: string;             // e.g., "Gender", "ActionReferenceMethod", "Nationality"
  Description: string | null;
  Locked: boolean;
}

/**
 * Skill - An individual skill/lookup value belonging to a SkillCategory
 * Skills are used for dropdown fields like Gender, Nationality, etc.
 */
export interface StandingDataSkill {
  SkillId: number;
  Skill: string;                // Display text (e.g., "Male", "Female")
  Description: string | null;   // Extended description (e.g., "Man/Boy")
  Locked: boolean;
  SkillCategoryId: number;      // Links to SkillCategory
  Category: string;             // Denormalized category name
  Ordering: number;
  FieldId: number;              // Links to FieldDescriptions.FieldId
}

/**
 * Field Description - Metadata for entity fields
 * Used by CRUD forms to determine how to render and validate each field.
 * The FieldId links to FieldDescriptionValues returned by /Entity/ReadEntity
 * 
 * CRITICAL: These field names match the actual API response from /Cache/StandingData
 */
export interface StandingDataFieldDescription {
  FieldId: number;              // Primary key - links to FieldDescriptionValues.FieldId
  TableFieldName: string;       // Technical field name (e.g., "Forenames", "ContactPhoto")
  TableName: string;            // Source table (e.g., "Contact", "ContactConfigFields")
  FieldTypeId: number;          // Type ID (1=text, 14=file, etc.)
  FieldTypeName: string;        // Type name ("Text", "Date", "CurrencyAmountPeriod", etc.)
  Description: string | null;   // Field description
  Fixed: boolean;               // Whether field is system-fixed
  FieldLength: number;          // Max length for text fields
  FieldLabel: string;           // Display label for UI
  Lookup: boolean;              // Whether field should show as dropdown
  AddToLookups: boolean;        // Whether new values can be added to lookups
  OvertypeLookups: boolean;     // Whether lookups allow freeform text
  MultiLine: boolean;           // For text fields - render as textarea
  MinValue: string | null;      // Minimum value for numeric/date fields
  MaxValue: string | null;      // Maximum value for numeric/date fields
  DefaultValue: string | null;  // Default value for new records
  Mandatory: boolean;           // Whether field is required
  DisplayFormat: string | null; // Display format (e.g., "#,##0.00")
  SpinIncrement: number;        // Increment for numeric spinner
  SortAlphabetically: boolean;  // Sort lookup options alphabetically
  SearchFieldName: string | null; // Field name used in searches
  SkillComboCategory: string | null; // Category name for Skills lookup
  Locked: boolean;              // Whether field is locked/readonly
  RequirementCandidateSearchVisible: boolean; // Visibility in candidate search
  Value: string | null;         // Current value (from ReadEntity)
  DisplayValue: string | null;  // Formatted display value (from ReadEntity)
}

/**
 * User - System user for lookups like "Added By", "Owner", etc.
 */
export interface StandingDataUser {
  UserId: number;
  FullName: string;
  LoginName: string;
  TelNo: string;
  ContactId: number;
  Locked: boolean;
  Photo: string | null;
}

/**
 * Entity Type - Types that can be assigned to entities (e.g., Customer, Candidate, Supplier)
 * Each EntityName can have multiple types assigned.
 */
export interface StandingDataEntityType {
  EntityTypeId: number;
  EntityName: string;         // The entity this type applies to ("Company", "Contact", etc.)
  Name: string;               // Display name ("Customer", "Supplier", etc.)
  Description: string | null;
  ForeColour: number;
  BackColour: number;
  Icon: string | null;
}

export interface StandingDataIndustry {
  IndustryId: number;
  Name: string;
  Description: string | null;
  Ordering: number;
}

/**
 * System setting from standing data
 */
export interface StandingDataSystemSetting {
  SystemSettingId: number;
  Name: string;
  Value: string | null;
  Description: string | null;
  Dirty: boolean;
}

/**
 * Field Type - Defines what kind of control/input is used for a field
 * Examples: Text, Integer, Date, Company, Contact, File Reference, etc.
 */
export interface StandingDataFieldType {
  FieldTypeId: number;
  Description: string;          // e.g., "Text", "Date", "Company"
  DisplayFormat: string | null; // e.g., "#,###,##0.00" for Currency
  Ordering: number;
}

/**
 * Entity Description - Metadata about entity types
 * Used to enumerate available entities in the system.
 */
export interface StandingDataEntityDescription {
  EntityId: number;
  EntityName: string;           // e.g., "Contact", "Company", "Requirement"
  Description: string;          // Human-readable description
  VirtualForm: boolean;         // If true, has no physical form
}

export interface StandingDataResponse {
  AddressTypes: StandingDataAddressType[];
  ContactTitles: StandingDataContactTitle[];
  ContactPriorities: StandingDataContactPriority[];
  ContactTypeColours: StandingDataContactTypeColour[];
  Currencies: StandingDataCurrency[];
  ConsultancyPeriods: StandingDataConsultancyPeriod[];
  ConsultancyPeriodRelationships: StandingDataConsultancyPeriodRelationship[];
  SkillCategories: StandingDataSkillCategory[];       // Categories linking Skills to FieldIds
  Skills: StandingDataSkill[];                        // Master skill list - filter by SkillCategoryId for lookups
  FieldDescriptions: StandingDataFieldDescription[];  // CRITICAL: Metadata for all entity fields
  FieldTypes?: StandingDataFieldType[];               // Field type definitions (Text, Date, Company, etc.)
  EntityDescriptions?: StandingDataEntityDescription[]; // Entity metadata
  Users: StandingDataUser[];                          // System users for "Added By", "Owner" lookups
  EntityTypes: StandingDataEntityType[];              // Entity types per entity (Customer, Candidate, etc.)
  Industries?: StandingDataIndustry[];                // Industry lookup values for Company.IndustryId field
  SystemSettings?: StandingDataSystemSetting[];       // System configuration settings
  Success: boolean;
  ErrorMessage: string | null;
}

// ===== Entity CRUD Types =====

/**
 * A single field value from an entity read response.
 * FieldId links to StandingDataFieldDescription.FieldId in cached Standing Data.
 */
export interface FieldDescriptionValue {
  FieldId: number;
  Value: string | number | boolean | null;
  DisplayValue: string | null;  // Human-readable text for lookup fields
}

/**
 * The entity object returned by /Entity/ReadEntity
 */
export interface EntityReadResult {
  EntityName: string;
  EntityId: number;
  FieldDescriptionValues: FieldDescriptionValue[];
}

/**
 * Response from POST /Entity/ReadEntity
 */
export interface EntityReadResponse {
  Operation: number;
  RecordId: number;
  EntityName: string;
  Entity: EntityReadResult;
  APICredentialValidation: number;
  Success: boolean;
  ErrorMessage: string | null;
}

/**
 * A skill assigned to an entity (from /Skills/ReadEntitySkills)
 */
export interface EntitySkill {
  SkillId: number;
  Skill: string;
  Description: string;
  Locked: boolean;
  SkillCategoryId: number;
  Category: string;
  Ordering: number;
  FieldId: number;
}

/**
 * Response from POST /Skills/ReadEntitySkills
 */
export interface EntitySkillsResponse {
  Skills: EntitySkill[];
  Success?: boolean;
  ErrorMessage?: string | null;
}

/**
 * Social network profile from POST /SocialNetwork/ReadPerContact
 */
export interface SocialNetworkProfile {
  SocialNetworkProfileId: number;
  ProfileNumber: number;
  ContactId: number;
  URL: string | null;
  ContactName: string | null;
  CompanyName: string | null;
  JobTitle: string | null;
  Summary: string | null;
  ProfileID: string | null;
  PhotoImage: string | null;
  Industry: string | null;
  Location: string | null;
  Connections: number;
  EMail: string | null;
  Twitter: string | null;
  WebSites: string | null;
  CandidateImport: boolean;
}

/**
 * Response from POST /SocialNetwork/ReadPerContact
 */
export interface SocialNetworkResponse {
  Profiles: SocialNetworkProfile[];
  ContactSummary: Record<string, unknown>;
  Success: boolean;
  ErrorMessage: string | null;
}

/**
 * Education history item from POST /Contact/EducationHistory
 */
export interface EducationHistoryItem {
  ContactEducationHistoryId: number;
  ContactId: number;
  Dates: string;
  School: string;
  Location: string;
  Qualifications: string;
  Comments: string | null;
  Ordering: number | null;
}

/**
 * Response from POST /Contact/EducationHistory
 */
export interface EducationHistoryResponse {
  List: EducationHistoryItem[];
  TotalRecordCount: number;
  TotalPageCount: number;
  PageNumber: number;
  RecordsPerPage: number;
  Success: boolean;
  ErrorMessage: string | null;
}

/**
 * Request for POST /Contacts/WriteEducationHistory
 */
export interface WriteEducationHistoryRequest {
  ContactEducationHistoryId: number;  // 0 for new, existing ID for update
  ContactId: number;
  Dates: string;
  School: string;
  Location: string;
  Qualifications: string;
  Comments: string | null;
}

/**
 * Response from POST /Contacts/WriteEducationHistory
 */
export interface WriteEducationHistoryResponse {
  ContactEducationHistoryId: number;
  Success: boolean;
  ErrorMessage: string | null;
}

/**
 * Response from POST /Contacts/DeleteEducationHistory
 */
export interface DeleteEducationHistoryResponse {
  Success: boolean;
  ErrorMessage: string | null;
}

/**
 * Work history item from POST /Contact/WorkHistory
 */
export interface WorkHistoryItem {
  ContactWorkHistoryId: number;
  ContactId: number;
  Dates: string;
  Employer: string;
  Location: string;
  JobTitle: string;
  Department: string | null;
  Description: string | null;
  Ordering: number | null;
  CompanyId: number | null;
}

/**
 * Response from POST /Contact/WorkHistory
 */
export interface WorkHistoryResponse {
  List: WorkHistoryItem[];
  TotalRecordCount: number;
  TotalPageCount: number;
  PageNumber: number;
  RecordsPerPage: number;
  Success: boolean;
  ErrorMessage: string | null;
}

/**
 * Request for POST /Contacts/WriteWorkHistory
 */
export interface WriteWorkHistoryRequest {
  ContactWorkHistoryId: number;  // 0 for new record
  ContactId: number;
  Dates: string;
  Employer: string;
  Location: string;
  JobTitle: string;
  Department: string | null;
  Description: string | null;
}

/**
 * Response from POST /Contacts/WriteWorkHistory
 */
export interface WriteWorkHistoryResponse {
  ContactWorkHistoryId: number;
  Success: boolean;
  ErrorMessage: string | null;
}

/**
 * Response from POST /Contacts/DeleteWorkHistory
 */
export interface DeleteWorkHistoryResponse {
  Success: boolean;
  ErrorMessage: string | null;
}

// ===== Contact Search Types =====

/**
 * Parameters for POST /Contacts/List with Boolean Search Tree support
 */
export interface ContactSearchParams {
  PageNumber: number;
  RecordsPerPage: number;
  SearchTreeEnabled?: boolean;
  SearchTreeJson?: string;
  SortColumnName?: string;
  SortAscending?: boolean;
}

/**
 * Response from POST /Contacts/List
 */
export interface ContactSearchResponse {
  List: Record<string, unknown>[];
  TotalRecordCount: number;
  TotalPageCount: number;
  FirstRowNumber: number;
  LastRowNumber: number;
  PageNumber: number;
  RecordsPerPage: number;
  SortColumnName: string | null;
  SortAscending: boolean;
  Success: boolean;
  ErrorMessage: string | null;
}

/**
 * Item from POST /Lookup/Find - universal entity search
 */
export interface LookupFindItem {
  EntityName: string;  // "Contact", "Company", "Requirement", etc.
  RecordId: number;
  Display: string;     // Formatted display string e.g. "Acme Corp, Staffing and Recruiting"
  EntityID: string | null;
}

/**
 * Response from POST /Lookup/Find
 */
export interface LookupFindResponse {
  Items: LookupFindItem[];
  Success: boolean;
  ErrorMessage: string | null;
}

// ===== Saved Search Types =====

/**
 * A saved search item from POST /EntitySearch/List
 * Represents a user's saved boolean search for an entity (Contact, Company, etc.)
 */
export interface SavedSearchItem {
  ApexEntitySearchId: number;       // Unique ID for this saved search
  EntityName: string;               // "Contact", "Company", "Requirement", etc.
  SearchName: string;               // Display name of the search
  SearchDescription: string | null; // Optional description
  DateLastUpdated: string;          // ISO date string
  DateSearchResultsSaved: string | null; // When search results were last saved
  UserName: string;                 // Owner of the saved search
  DefaultSearch: boolean;           // Whether this is the default search for the entity
}

/**
 * Response from POST /EntitySearch/List - paginated list of saved searches
 */
export interface SavedSearchListResponse {
  List: SavedSearchItem[];
  TotalRecordCount: number;
  TotalPageCount: number;
  FirstRowNumber: number;
  LastRowNumber: number;
  PageNumber: number;
  RecordsPerPage: number;
  SortColumnName: string | null;
  SortAscending: boolean;
  Success: boolean;
  ErrorMessage: string | null;
}

/**
 * The criteria object inside a SearchTree read response
 */
export interface SearchTreeCriteria {
  ApexSearchTreeId: number;
  Description: string | null;
  JsonCriteria: string;             // JSON string containing the search tree
  UserId: number;
  ControlId: string;
  EntityName: string;
  RecordId: number;
  CloneMatchingControl: boolean;
}

/**
 * Response from POST /SearchTree/Read
 */
export interface SearchTreeReadResponse {
  Criteria: SearchTreeCriteria | null;
  Success: boolean;
  ErrorMessage: string | null;
}

// ===== Session Expired Error =====

export class SessionExpiredError extends Error {
  constructor(message: string = 'Session expired') {
    super(message);
    this.name = 'SessionExpiredError';
  }
}

// ===== TriSys Web API Service =====

class TriSysWebAPIService {
  private userProfile: UserProfile | null = null;
  private standingData: StandingDataResponse | null = null;

  /**
   * Get the active API base URL.
   * Uses the Vertical Stack Server endpoint if one was returned during authentication,
   * otherwise falls back to the default API_CONFIG.baseUrl.
   */
  private getActiveBaseUrl(): string {
    const storedEndpoint = sessionStorage.getItem(API_ENDPOINT_KEY);
    return storedEndpoint || API_CONFIG.baseUrl;
  }

  /**
   * Apply a Vertical Stack Server endpoint redirect from an authenticate response.
   * If WebAPIEndPoint is non-null, all subsequent API calls will use this endpoint.
   */
  private applyEndpointRedirect(response: TriSysAuthResponse): void {
    if (response.WebAPIEndPoint) {
      const endpoint = response.WebAPIEndPoint.replace(/\/+$/, '');
      sessionStorage.setItem(API_ENDPOINT_KEY, endpoint);
      logger.info('Vertical Stack Server redirect applied', { endpoint });
    }
  }

  /**
   * Make a public request to the TriSys Web API
   * Used by tenant-specific forms to call custom endpoints (e.g., DashboardMetric APIs)
   */
  public async request<T>(endpoint: string, body?: object): Promise<T> {
    return this.makeRequest<T>(endpoint, body);
  }

  /**
   * Make a request to the TriSys Web API (internal implementation)
   */
  private async makeRequest<T>(endpoint: string, body?: object, method: 'POST' | 'DELETE' = 'POST'): Promise<T> {
    const headers: Record<string, string> = {
      'SiteKey': getSiteKey(),
      'Content-Type': 'application/json'
    };

    // Add session key if available
    const sessionKey = this.getSessionKey();
    if (sessionKey) {
      headers['DataServicesKey'] = sessionKey;
    }

    const activeBaseUrl = this.getActiveBaseUrl();
    const url = `${activeBaseUrl}${endpoint}`;
    logger.debug('API Request:', endpoint);

    try {
      const response = await fetch(url, {
        method,
        headers,
        body: body ? JSON.stringify(body) : undefined
      });

      const text = await response.text();

      // Check for HTTP errors (4xx, 5xx)
      if (!response.ok) {
        logger.error('API HTTP error', { 
          status: response.status, 
          statusText: response.statusText,
          responseText: text.substring(0, 500)
        });
        // Try to parse as JSON to get error message
        try {
          const errorJson = JSON.parse(text);
          if (errorJson.ErrorMessage) {
            throw new Error(errorJson.ErrorMessage);
          }
        } catch {
          // Not JSON, use status text
        }
        throw new Error(`API error ${response.status}: ${response.statusText}`);
      }

      // Check for session expiry
      if (text.startsWith('Expired DataServicesKey:')) {
        logger.warn('Session expired, clearing session');
        this.clearSession();
        throw new SessionExpiredError(text);
      }

      // Parse JSON response
      try {
        return JSON.parse(text) as T;
      } catch {
        logger.error('Failed to parse API response:', text);
        throw new Error(`Invalid API response: ${text.substring(0, 100)}`);
      }
    } catch (error) {
      if (error instanceof SessionExpiredError) {
        throw error;
      }
      logger.error('API request failed:', error);
      throw error;
    }
  }

  /**
   * Make a form-encoded request to the TriSys Web API
   * Used for social login which requires application/x-www-form-urlencoded format
   */
  private async requestFormEncoded<T>(endpoint: string, formData: URLSearchParams): Promise<T> {
    const headers: Record<string, string> = {
      'SiteKey': getSiteKey(),
      'Content-Type': 'application/x-www-form-urlencoded'
    };

    // Add session key if available
    const sessionKey = this.getSessionKey();
    if (sessionKey) {
      headers['DataServicesKey'] = sessionKey;
    }

    const activeBaseUrl = this.getActiveBaseUrl();
    const url = `${activeBaseUrl}${endpoint}`;
    logger.debug('API Request (form-encoded):', endpoint);

    try {
      const response = await fetch(url, {
        method: 'POST',
        headers,
        body: formData.toString()
      });

      const text = await response.text();

      // Check for session expiry
      if (text.startsWith('Expired DataServicesKey:')) {
        logger.warn('Session expired, clearing session');
        this.clearSession();
        throw new SessionExpiredError(text);
      }

      // Parse JSON response
      try {
        return JSON.parse(text) as T;
      } catch {
        logger.error('Failed to parse API response:', text);
        throw new Error(`Invalid API response: ${text.substring(0, 100)}`);
      }
    } catch (error) {
      if (error instanceof SessionExpiredError) {
        throw error;
      }
      logger.error('API request failed:', error);
      throw error;
    }
  }

  // ===== Session Management =====

  /**
   * Get the configured storage type from OEM config
   */
  private getStorageType(): SessionStorageType {
    try {
      return oemConfig.getSessionStorageType();
    } catch {
      // OEM config not loaded yet - default to session storage
      return 'session';
    }
  }

  /**
   * Get the current session key from configured storage
   * - 'memory': Returns from in-memory variable (lost on F5)
   * - 'session': Returns from sessionStorage (lost on tab close)
   * - 'local': Returns from localStorage (persists until cleared)
   */
  public getSessionKey(): string | null {
    const storageType = this.getStorageType();
    
    switch (storageType) {
      case 'memory':
        return memorySessionKey;
      case 'local':
        return localStorage.getItem(SESSION_KEY);
      case 'session':
      default:
        return sessionStorage.getItem(SESSION_KEY);
    }
  }

  /**
   * Store the session key in configured storage
   */
  private setSessionKey(key: string): void {
    const storageType = this.getStorageType();
    
    switch (storageType) {
      case 'memory':
        memorySessionKey = key;
        logger.debug('Session key stored in memory (will be lost on page refresh)');
        break;
      case 'local':
        localStorage.setItem(SESSION_KEY, key);
        logger.debug('Session key stored in localStorage (persists until cleared)');
        break;
      case 'session':
      default:
        sessionStorage.setItem(SESSION_KEY, key);
        logger.debug('Session key stored in sessionStorage (survives refresh, lost on tab close)');
        break;
    }
  }

  /**
   * Clear the session key, user profile, and standing data from all storage types
   */
  public clearSession(): void {
    // Clear from all storage types to ensure clean logout
    memorySessionKey = null;
    sessionStorage.removeItem(SESSION_KEY);
    localStorage.removeItem(SESSION_KEY);
    
    // Clear Vertical Stack Server endpoint redirect
    sessionStorage.removeItem(API_ENDPOINT_KEY);
    
    // Clear cached user profile
    this.userProfile = null;
    sessionStorage.removeItem(POST_LOGIN_CREDS_KEY);
    
    // Clear cached standing data
    this.standingData = null;
    sessionStorage.removeItem(STANDING_DATA_KEY);
    
    logger.debug('Session and cached data cleared from all storage types');
  }

  /**
   * Check if user is currently authenticated
   */
  public isAuthenticated(): boolean {
    return this.getSessionKey() !== null;
  }

  // ===== Authentication =====

  /**
   * Authenticate with email and password
   */
  public async loginWithCredentials(email: string, password: string, accountType?: number): Promise<TriSysAuthResponse> {
    logger.debug('Authenticating with credentials:', email);

    // Clear any cached profile from previous user before new login
    this.userProfile = null;

    const payload: Record<string, unknown> = {
      FullName: email,
      Password: password
    };

    if (accountType !== undefined) {
      payload.AccountType = accountType;
    }

    const response = await this.makeRequest<TriSysAuthResponse>('/security/authenticate', payload);

    if (response.Success && response.DataServicesKey) {
      this.setSessionKey(response.DataServicesKey);
      this.applyEndpointRedirect(response);
      logger.debug('Login successful');
    } else {
      logger.warn('Login failed:', response.ErrorMessage);
    }

    return response;
  }

  /**
   * Authenticate with Auth0 token (social login)
   * Note: This uses URL-encoded form data, not JSON, to match the legacy Flexiva API format
   */
  public async loginWithAuth0(email: string, auth0UserObject: object, accountType?: number): Promise<TriSysAuthResponse> {
    logger.debug('Authenticating with Auth0:', email);

    // Clear any cached profile from previous user before new login
    this.userProfile = null;

    // Extract the rawClaims from the Auth0 user object (contains all Auth0 fields + authToken)
    const auth0User = auth0UserObject as { rawClaims?: Record<string, unknown>; authToken?: string };
    
    // Use rawClaims if available, otherwise fall back to the whole object
    const claimsToSend = auth0User.rawClaims || auth0UserObject;
    
    if (!claimsToSend) {
      logger.error('No claims found in Auth0 user object');
      return {
        DataServicesKey: null,
        CredentialsCorrectButSomethingElsePreventsLogin: false,
        Success: false,
        ErrorMessage: 'No authentication data received from Auth0'
      };
    }

    // Base64 encode the claims object (JSON stringified)
    const claimsJson = JSON.stringify(claimsToSend);
    const auth0Base64 = btoa(claimsJson);

    // Build URL-encoded form data (NOT JSON) - this is required by TriSys API for social login
    const formData = new URLSearchParams();
    formData.append('FullName', '');
    formData.append('Password', '');
    formData.append('SocialNetworkLoginParameters', '');
    formData.append('Auth0UserObjectBase64', auth0Base64);
    formData.append('CustomFolderMandatory', 'false');
    formData.append('URL', window.location.href);
    formData.append('ApplicationName', accountType === 12 ? 'API-Hub' : 'Flexiva');
    if (accountType !== undefined) {
      formData.append('AccountType', accountType.toString());
    }

    // Make request with form-encoded content type
    const response = await this.requestFormEncoded<TriSysAuthResponse>('/security/authenticate', formData);

    if (response.Success && response.DataServicesKey) {
      this.setSessionKey(response.DataServicesKey);
      this.applyEndpointRedirect(response);
      logger.debug('Auth0 login successful');
    } else {
      logger.warn('Auth0 login failed:', response.ErrorMessage);
    }

    return response;
  }

  /**
   * Verify the current session is still valid
   */
  public async verifySession(): Promise<boolean> {
    if (!this.getSessionKey()) {
      return false;
    }

    try {
      const response = await this.makeRequest<TriSysVerifyResponse>('/security/VerifyDataServicesKey');
      
      if (!response.Success) {
        logger.debug('Session verification failed:', response.ErrorMessage);
        this.clearSession();
        return false;
      }

      logger.debug('Session verified');
      return true;
    } catch (error) {
      if (error instanceof SessionExpiredError) {
        return false;
      }
      logger.error('Session verification error:', error);
      return false;
    }
  }

  /**
   * Logout - call backend API then clear local session
   * The API call is best-effort - we always clear the local session regardless
   */
  public async logout(): Promise<void> {
    // Only attempt API call if we have a session key
    if (this.getSessionKey()) {
      try {
        await this.makeRequest<{ Success: boolean; ErrorMessage: string | null }>('/security/logoff', {
          RetainSessionKey: true
        });
        logger.debug('Logoff API call successful');
      } catch (error) {
        // Ignore errors - we still want to clear the local session
        logger.debug('Logoff API call failed (ignored):', error);
      }
    }

    // Always clear local session
    this.clearSession();
    logger.debug('Logged out');
  }

  // ===== Public Forms =====

  /**
   * Submit a public enquiry (contact form)
   */
  public async submitEnquiry(payload: any): Promise<any> {
    return this.makeRequest<any>('/Enquiry/EnquiryRequest', payload);
  }

  // ===== API Info =====

  /**
   * Get the API version information
   */
  public async getVersion(): Promise<TriSysVersionResponse> {
    return this.makeRequest<TriSysVersionResponse>('/start/versiondetails');
  }

  // ===== User Profile =====

  /**
   * Get the logged-in user's profile
   * Caches the result after first call
   */
  public async getUserProfile(forceRefresh = false): Promise<UserProfile | null> {
    if (this.userProfile && !forceRefresh) {
      return this.userProfile;
    }

    if (!this.getSessionKey()) {
      return null;
    }

    try {
      const response = await this.makeRequest<TriSysCredentialsResponse>('/security/PostLoginCRMCredentials');

      // Log the raw response to debug
      logger.info('PostLoginCRMCredentials raw response:', JSON.stringify(response, null, 2));

      // This endpoint returns data directly, not wrapped in Success/ErrorMessage
      // Check for LoggedInUser presence (the actual data we need)
      if (!response.LoggedInUser) {
        logger.warn('Failed to get user profile: No LoggedInUser in response');
        return null;
      }

      // Store raw credentials in sessionStorage for debugging/raw settings view
      sessionStorage.setItem(POST_LOGIN_CREDS_KEY, JSON.stringify(response));

      const user = response.LoggedInUser;
      const contact = user.Contact;
      const crmContact = user.CRMContact;

      // Debug: Log Contact object fields
      logger.info('Contact fields:', {
        FullName: contact?.FullName,
        JobTitle: contact?.JobTitle,
        CompanyName: contact?.CompanyName,
        ContactPhoto: contact?.ContactPhoto
      });

      // Display name: Contact.FullName is "Josie Musto", NOT user.FullName which is email
      // See specifications/TriSys-Web-API-Integration.md for API response structure
      const displayName = contact?.FullName || user.ForenameAndSurname || user.LoginName;
      
      // ContactPhoto is the primary avatar source
      const avatar = contact?.ContactPhoto || contact?.ContactPhotoURL || null;
      
      // JobTitle from Contact object
      const jobTitle = contact?.JobTitle || crmContact?.JobTitle || '';
      
      // CompanyName from Contact object
      const companyName = contact?.CompanyName || crmContact?.CompanyName || '';
      
      logger.info('Extracted profile values:', { displayName, jobTitle, companyName, avatar });

      this.userProfile = {
        userId: user.UserId,
        contactId: user.ContactId,
        loginName: user.LoginName,
        displayName,
        email: contact?.WorkEMail || '',
        jobTitle,
        companyName,
        avatar,
        companyLogo: contact?.CompanyLogoURL || null,
        linkedIn: contact?.LinkedIn || null,
        isAdmin: crmContact?.Subscriber?.AccountMaintainer ?? false,
        isDeveloper: crmContact?.ApexDeveloper ?? false,
        showDummyData: response.ShowDummyDashboardData,
        isTrialAccount: response.TrialAccount
      };

      logger.info('User profile loaded:', {
        displayName: this.userProfile.displayName,
        jobTitle: this.userProfile.jobTitle,
        avatar: this.userProfile.avatar
      });
      return this.userProfile;
    } catch (error) {
      logger.error('Failed to load user profile:', error);
      return null;
    }
  }

  /**
   * Get cached user profile (doesn't make API call)
   */
  public getCachedProfile(): UserProfile | null {
    return this.userProfile;
  }

  // ===== Standing Data =====

  /**
   * Get standing data (reference data for lookups and entity CRUD)
   * Caches the result after first call - should be called after login
   * See: https://api.trisys.co.uk/Cache/StandingData
   */
  public async getStandingData(forceRefresh = false): Promise<StandingDataResponse | null> {
    console.log('[DEBUG getStandingData] CALLED, forceRefresh:', forceRefresh);
    console.log('[DEBUG getStandingData] this.standingData:', this.standingData ? 'exists' : 'NULL');
    console.log('[DEBUG getStandingData] sessionKey:', this.getSessionKey() ? 'exists' : 'NULL');
    
    // Return cached data if available and not forcing refresh
    if (this.standingData && !forceRefresh) {
      console.log('[DEBUG getStandingData] Returning in-memory cache');
      return this.standingData;
    }

    // Try to restore from sessionStorage first (survives page refresh)
    if (!forceRefresh) {
      const stored = sessionStorage.getItem(STANDING_DATA_KEY);
      if (stored) {
        try {
          this.standingData = JSON.parse(stored);
          logger.debug('Standing data restored from sessionStorage');
          return this.standingData;
        } catch (e) {
          logger.warn('Failed to parse stored standing data, fetching fresh');
        }
      }
    }

    if (!this.getSessionKey()) {
      logger.warn('Cannot fetch standing data: not authenticated');
      return null;
    }

    try {
      console.log('[DEBUG getStandingData] Making API call to /Cache/StandingData...');
      logger.debug('Fetching standing data from API...');
      const response = await this.makeRequest<StandingDataResponse>('/Cache/StandingData');
      
      console.log('[DEBUG getStandingData] API response Success:', response.Success);
      console.log('[DEBUG getStandingData] API response ContactTitles:', response.ContactTitles ? `${response.ContactTitles.length} items` : 'MISSING');
      if (response.ContactTitles) {
        console.log('[DEBUG getStandingData] First 5 titles:', response.ContactTitles.slice(0, 5).map(t => t.Name));
      }

      // Check if we got valid data - the API may not return Success field
      // Instead, check if we got the essential arrays
      if (response.ErrorMessage) {
        logger.warn('Failed to get standing data:', response.ErrorMessage);
        return null;
      }
      
      // Validate we got at least some expected data
      if (!response.ContactTitles && !response.FieldDescriptions) {
        logger.warn('Standing data response missing expected arrays');
        return null;
      }

      // Cache the response
      this.standingData = response;
      sessionStorage.setItem(STANDING_DATA_KEY, JSON.stringify(response));

      logger.info('Standing data loaded and cached:', {
        addressTypes: response.AddressTypes?.length ?? 0,
        contactTitles: response.ContactTitles?.length ?? 0,
        contactPriorities: response.ContactPriorities?.length ?? 0,
        currencies: response.Currencies?.length ?? 0,
        consultancyPeriods: response.ConsultancyPeriods?.length ?? 0,
        skillCategories: response.SkillCategories?.length ?? 0,
        skills: response.Skills?.length ?? 0,
        fieldDescriptions: response.FieldDescriptions?.length ?? 0,
        users: response.Users?.length ?? 0
      });

      return this.standingData;
    } catch (error) {
      logger.error('Failed to load standing data:', error);
      return null;
    }
  }

  /**
   * Get cached standing data (doesn't make API call).
   * First checks in-memory cache, then falls back to sessionStorage.
   * This ensures data survives page refresh.
   */
  public getCachedStandingData(): StandingDataResponse | null {
    // DEBUG: Check sessionStorage directly
    const storedRaw = sessionStorage.getItem(STANDING_DATA_KEY);
    console.log('[DEBUG getCachedStandingData] sessionStorage has trisys_standing_data:', storedRaw ? `${storedRaw.length} bytes` : 'NO');
    console.log('[DEBUG getCachedStandingData] in-memory this.standingData:', this.standingData ? 'exists' : 'NULL');
    
    // Return in-memory cache if available
    if (this.standingData) {
      return this.standingData;
    }

    // Try to restore from sessionStorage (survives page refresh)
    const stored = sessionStorage.getItem(STANDING_DATA_KEY);
    if (stored) {
      try {
        this.standingData = JSON.parse(stored);
        logger.debug('Standing data restored from sessionStorage in getCachedStandingData()');
        return this.standingData;
      } catch (e) {
        logger.warn('Failed to parse stored standing data:', e);
      }
    }

    return null;
  }

  // ===== Entity CRUD Operations =====

  /**
   * Read a single entity record using the generic Entity Read endpoint.
   * Returns FieldDescriptionValues where FieldId links to cached FieldDescriptions.
   * 
   * @param entityName - The entity type: "Contact", "Company", "Requirement", etc.
   * @param entityId - The unique identifier of the record to read
   * @returns EntityReadResponse with FieldDescriptionValues array
   * 
   * @example
   * const response = await trisysAPI.readEntity('Contact', 49346);
   * if (response.Success) {
   *   const fieldValues = response.Entity.FieldDescriptionValues;
   *   // Use FieldId to look up field metadata in getCachedStandingData().FieldDescriptions
   * }
   */
  public async readEntity(entityName: string, entityId: number): Promise<EntityReadResponse> {
    logger.debug(`Reading entity: ${entityName} #${entityId}`);

    const response = await this.makeRequest<EntityReadResponse>('/Entity/ReadEntity', {
      EntityName: entityName,
      EntityId: entityId
    });

    if (response.Success && response.Entity?.FieldDescriptionValues) {
      const fieldCount = response.Entity.FieldDescriptionValues.length;
      logger.debug(`Entity read successful: ${entityName} #${entityId}`, {
        fieldCount,
        // Log field IDs with non-null values for debugging
        fieldsWithValues: response.Entity.FieldDescriptionValues
          .filter(fv => fv.Value !== null && fv.Value !== '')
          .map(fv => ({ id: fv.FieldId, value: fv.Value, display: fv.DisplayValue }))
          .slice(0, 20) // First 20 only to avoid log spam
      });
    } else if (!response.Success) {
      logger.warn(`Entity read failed: ${entityName} #${entityId}`, { 
        error: response.ErrorMessage,
        fullResponse: response 
      });
    } else {
      logger.warn(`Entity read returned empty data: ${entityName} #${entityId}`, { response });
    }

    return response;
  }

  /**
   * Read skills assigned to an entity (Contact, Company, Requirement, etc.)
   * Skills are grouped by Category which can be used to build a tree view.
   * 
   * @param entityName - The entity type: "Contact", "Company", "Requirement", etc.
   * @param recordId - The unique identifier of the record
   * @returns EntitySkillsResponse with Skills array
   * 
   * @example
   * const response = await trisysAPI.readEntitySkills('Contact', 49346);
   * if (response.Skills) {
   *   // Group skills by Category for tree view
   *   const byCategory = response.Skills.reduce((acc, skill) => {
   *     if (!acc[skill.Category]) acc[skill.Category] = [];
   *     acc[skill.Category].push(skill);
   *     return acc;
   *   }, {} as Record<string, EntitySkill[]>);
   * }
   */
  public async readEntitySkills(entityName: string, recordId: number): Promise<EntitySkillsResponse> {
    logger.debug(`Reading entity skills: ${entityName} #${recordId}`);

    try {
      const response = await this.makeRequest<EntitySkillsResponse>('/Skills/ReadEntitySkills', {
        EntityName: entityName,
        RecordId: recordId
      });

      if (response.Skills) {
        logger.debug(`Entity skills loaded: ${entityName} #${recordId}`, {
          skillCount: response.Skills.length,
          categories: [...new Set(response.Skills.map(s => s.Category))]
        });
      } else {
        logger.warn(`Entity skills returned empty: ${entityName} #${recordId}`, { response });
      }

      return response;
    } catch (error) {
      logger.error(`Failed to read entity skills: ${entityName} #${recordId}`, { error });
      return {
        Skills: [],
        Success: false,
        ErrorMessage: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  }

  /**
   * Write (save) skills assigned to an entity (Contact, Company, Requirement, etc.)
   * This replaces ALL skills for the entity with the provided list.
   * 
   * @param entityName - The entity type: "Contact", "Company", "Requirement", etc.
   * @param recordId - The unique identifier of the record
   * @param skills - Array of skills to assign to the entity
   * @returns Success response
   * 
   * @example
   * const response = await trisysAPI.writeEntitySkills('Contact', 49346, [
   *   { SkillId: 141951, Skill: 'A Levels/Grades', ... }
   * ]);
   * if (response.Success) {
   *   ToasterService.success('Skills saved');
   * }
   */
  public async writeEntitySkills(
    entityName: string,
    recordId: number,
    skills: EntitySkill[]
  ): Promise<{ Success: boolean; ErrorMessage: string | null }> {
    logger.debug(`Writing entity skills: ${entityName} #${recordId}`, { skillCount: skills.length });

    try {
      const response = await this.makeRequest<{ Success: boolean; ErrorMessage: string | null }>('/Skills/WriteEntitySkills', {
        EntityName: entityName,
        RecordId: recordId,
        Skills: skills
      });

      if (response.Success) {
        logger.info(`Entity skills saved: ${entityName} #${recordId}`, {
          skillCount: skills.length,
          skills: skills.map(s => s.Skill)
        });
      } else {
        logger.error(`Failed to save entity skills: ${entityName} #${recordId}`, {
          errorMessage: response.ErrorMessage
        });
      }

      return response;
    } catch (error) {
      logger.error(`Error writing entity skills: ${entityName} #${recordId}`, { error });
      return {
        Success: false,
        ErrorMessage: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  }

  /**
   * Read social network profiles for a contact.
   * Uses POST /SocialNetwork/ReadPerContact
   * 
   * @param contactId - The Contact ID to read profiles for
   * @returns SocialNetworkResponse with Profiles array
   * 
   * @example
   * const response = await trisysAPI.readSocialProfiles(49346);
   * for (const profile of response.Profiles) {
   *   console.log(profile.URL);
   * }
   */
  public async readSocialProfiles(contactId: number): Promise<SocialNetworkResponse> {
    logger.debug(`Reading social profiles: Contact #${contactId}`);

    try {
      const response = await this.makeRequest<SocialNetworkResponse>('/SocialNetwork/ReadPerContact', {
        ContactId: contactId
      });

      if (response.Profiles) {
        logger.debug(`Social profiles loaded: Contact #${contactId}`, {
          profileCount: response.Profiles.length,
          urls: response.Profiles.map(p => p.URL).filter(Boolean)
        });
      } else {
        logger.warn(`Social profiles returned empty: Contact #${contactId}`, { response });
      }

      return response;
    } catch (error) {
      logger.error(`Failed to read social profiles: Contact #${contactId}`, { error });
      return {
        Profiles: [],
        ContactSummary: {},
        Success: false,
        ErrorMessage: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  }

  /**
   * Read education history for a contact.
   * Uses POST /Contact/EducationHistory
   * 
   * @param contactId - The Contact ID to read education history for
   * @param pageNumber - Page number for pagination (default 1)
   * @param recordsPerPage - Records per page (default 10)
   * @returns EducationHistoryResponse with List array and pagination info
   * 
   * @example
   * const response = await trisysAPI.readEducationHistory(49343, 1, 10);
   * for (const edu of response.List) {
   *   console.log(`${edu.Dates}: ${edu.School} - ${edu.Qualifications}`);
   * }
   */
  public async readEducationHistory(
    contactId: number,
    pageNumber: number = 1,
    recordsPerPage: number = 10
  ): Promise<EducationHistoryResponse> {
    logger.debug(`Reading education history: Contact #${contactId}`, { pageNumber, recordsPerPage });

    try {
      const response = await this.makeRequest<EducationHistoryResponse>('/Contact/EducationHistory', {
        ContactId: contactId,
        PageNumber: pageNumber,
        RecordsPerPage: recordsPerPage
      });

      if (response.List) {
        logger.debug(`Education history loaded: Contact #${contactId}`, {
          count: response.List.length
        });
      } else {
        logger.warn(`Education history returned empty: Contact #${contactId}`, { response });
      }

      return response;
    } catch (error) {
      logger.error(`Failed to read education history: Contact #${contactId}`, { error });
      return {
        List: [],
        TotalRecordCount: 0,
        TotalPageCount: 0,
        PageNumber: 1,
        RecordsPerPage: 10,
        Success: false,
        ErrorMessage: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  }

  /**
   * Write (create or update) an education history record.
   * Uses POST /Contacts/WriteEducationHistory
   * 
   * @param record - The education history data to save
   * @returns WriteEducationHistoryResponse with the record ID and success status
   * 
   * @example
   * // Create new record
   * const response = await trisysAPI.writeEducationHistory({
   *   ContactEducationHistoryId: 0,
   *   ContactId: 49346,
   *   Dates: '2020-2022',
   *   School: 'University of Bath',
   *   Location: 'UK',
   *   Qualifications: 'MBA',
   *   Comments: 'Masters in Business Administration'
   * });
   * 
   * // Update existing record
   * const response = await trisysAPI.writeEducationHistory({
   *   ContactEducationHistoryId: 562,
   *   ContactId: 49346,
   *   Dates: '2020-2022',
   *   School: 'University of Bath',
   *   Location: 'West UK',
   *   Qualifications: 'MBA',
   *   Comments: 'Updated comments'
   * });
   */
  public async writeEducationHistory(record: WriteEducationHistoryRequest): Promise<WriteEducationHistoryResponse> {
    const isNew = record.ContactEducationHistoryId === 0;
    logger.debug(`Writing education history: ${isNew ? 'NEW' : `ID #${record.ContactEducationHistoryId}`}`, { 
      contactId: record.ContactId,
      school: record.School 
    });

    try {
      const response = await this.makeRequest<WriteEducationHistoryResponse>('/Contacts/WriteEducationHistory', record);

      if (response.Success) {
        logger.info(`Education history ${isNew ? 'created' : 'updated'}: ID #${response.ContactEducationHistoryId}`);
      } else {
        logger.warn(`Education history write failed`, { error: response.ErrorMessage });
      }

      return response;
    } catch (error) {
      logger.error(`Failed to write education history`, { error, record });
      return {
        ContactEducationHistoryId: record.ContactEducationHistoryId,
        Success: false,
        ErrorMessage: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  }

  /**
   * Delete an education history record.
   * Uses POST /Contacts/DeleteEducationHistory
   * 
   * @param contactEducationHistoryId - The ID of the education record to delete
   * @returns DeleteEducationHistoryResponse with success status
   * 
   * @example
   * const response = await trisysAPI.deleteEducationHistory(568);
   * if (response.Success) {
   *   console.log('Education record deleted');
   * }
   */
  public async deleteEducationHistory(contactEducationHistoryId: number): Promise<DeleteEducationHistoryResponse> {
    logger.debug(`Deleting education history: ID #${contactEducationHistoryId}`);

    try {
      const response = await this.makeRequest<DeleteEducationHistoryResponse>(
        '/Contacts/DeleteEducationHistory',
        { ContactEducationHistoryId: contactEducationHistoryId }
      );

      if (response.Success) {
        logger.info(`Education history deleted: ID #${contactEducationHistoryId}`);
      } else {
        logger.warn(`Education history delete failed`, { error: response.ErrorMessage });
      }

      return response;
    } catch (error) {
      logger.error(`Failed to delete education history: ID #${contactEducationHistoryId}`, { error });
      return {
        Success: false,
        ErrorMessage: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  }

  /**
   * Read work history for a contact.
   * Uses POST /Contact/WorkHistory
   * 
   * @param contactId - The Contact ID to read work history for
   * @param pageNumber - Page number for pagination (default 1)
   * @param recordsPerPage - Records per page (default 10)
   * @returns WorkHistoryResponse with List array and pagination info
   * 
   * @example
   * const response = await trisysAPI.readWorkHistory(49343, 1, 10);
   * for (const job of response.List) {
   *   console.log(`${job.Dates}: ${job.Employer} - ${job.JobTitle}`);
   * }
   */
  public async readWorkHistory(
    contactId: number,
    pageNumber: number = 1,
    recordsPerPage: number = 10
  ): Promise<WorkHistoryResponse> {
    logger.debug(`Reading work history: Contact #${contactId}`, { pageNumber, recordsPerPage });

    try {
      const response = await this.makeRequest<WorkHistoryResponse>('/Contact/WorkHistory', {
        ContactId: contactId,
        PageNumber: pageNumber,
        RecordsPerPage: recordsPerPage
      });

      if (response.List) {
        logger.debug(`Work history loaded: Contact #${contactId}`, {
          count: response.List.length
        });
      } else {
        logger.warn(`Work history returned empty: Contact #${contactId}`, { response });
      }

      return response;
    } catch (error) {
      logger.error(`Failed to read work history: Contact #${contactId}`, { error });
      return {
        List: [],
        TotalRecordCount: 0,
        TotalPageCount: 0,
        PageNumber: 1,
        RecordsPerPage: 10,
        Success: false,
        ErrorMessage: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  }

  /**
   * Write (create or update) a work history record.
   * Uses POST /Contacts/WriteWorkHistory
   * 
   * @param request - The work history data to save
   * @returns WriteWorkHistoryResponse with Success status and ContactWorkHistoryId
   * 
   * @example
   * const response = await trisysAPI.writeWorkHistory({
   *   ContactWorkHistoryId: 0, // 0 for new record
   *   ContactId: 49343,
   *   Dates: '1987 - 1993',
   *   Employer: 'Tower Thistle Hotel',
   *   Location: 'London',
   *   JobTitle: 'Duty Manager',
   *   Department: 'Management',
   *   Description: 'Overseeing the day to day running of the hotel...'
   * });
   */
  public async writeWorkHistory(request: WriteWorkHistoryRequest): Promise<WriteWorkHistoryResponse> {
    const isNew = request.ContactWorkHistoryId === 0;
    logger.debug(`${isNew ? 'Creating' : 'Updating'} work history: Contact #${request.ContactId}`, { request });

    try {
      const response = await this.makeRequest<WriteWorkHistoryResponse>(
        '/Contacts/WriteWorkHistory',
        request
      );

      if (response.Success) {
        logger.info(`Work history ${isNew ? 'created' : 'updated'}: ID #${response.ContactWorkHistoryId}`);
      } else {
        logger.warn(`Work history write failed`, { error: response.ErrorMessage });
      }

      return response;
    } catch (error) {
      logger.error(`Failed to write work history: Contact #${request.ContactId}`, { error });
      return {
        ContactWorkHistoryId: 0,
        Success: false,
        ErrorMessage: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  }

  /**
   * Delete a work history record.
   * Uses POST /Contacts/DeleteWorkHistory
   * 
   * @param contactWorkHistoryId - The ID of the work history record to delete
   * @returns DeleteWorkHistoryResponse with success status
   * 
   * @example
   * const response = await trisysAPI.deleteWorkHistory(123);
   * if (response.Success) {
   *   console.log('Work history deleted');
   * }
   */
  public async deleteWorkHistory(contactWorkHistoryId: number): Promise<DeleteWorkHistoryResponse> {
    logger.debug(`Deleting work history: ID #${contactWorkHistoryId}`);

    try {
      const response = await this.makeRequest<DeleteWorkHistoryResponse>(
        '/Contacts/DeleteWorkHistory',
        { ContactWorkHistoryId: contactWorkHistoryId }
      );

      if (response.Success) {
        logger.info(`Work history deleted: ID #${contactWorkHistoryId}`);
      } else {
        logger.warn(`Work history delete failed`, { error: response.ErrorMessage });
      }

      return response;
    } catch (error) {
      logger.error(`Failed to delete work history: ID #${contactWorkHistoryId}`, { error });
      return {
        Success: false,
        ErrorMessage: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  }

  /**
   * Search for entities using the universal lookup.
   * Uses POST /Lookup/Find - returns Contacts, Companies, Requirements matching the search term.
   * 
   * @param expression - Search expression (e.g., "jo" to find "Johnson", "Jones", etc.)
   * @param maxPerEntity - Maximum records per entity type (default 16)
   * @param entityFilter - Optional: filter to specific entity type ("Contact", "Company", "Requirement")
   * @returns LookupFindResponse with Items array
   * 
   * @example
   * // Search for companies matching "opus"
   * const response = await trisysAPI.lookupFind('opus', 10, 'Company');
   * for (const item of response.Items) {
   *   console.log(`${item.RecordId}: ${item.Display}`);
   * }
   */
  public async lookupFind(
    expression: string, 
    maxPerEntity: number = 16,
    entityFilter?: 'Contact' | 'Company' | 'Requirement'
  ): Promise<LookupFindResponse> {
    logger.debug(`Lookup find: "${expression}"`, { maxPerEntity, entityFilter });

    try {
      const response = await this.makeRequest<LookupFindResponse>('/Lookup/Find', {
        Expression: expression,
        MaximumRecordsPerEntity: maxPerEntity
      });

      // Filter by entity type if specified
      if (entityFilter && response.Items) {
        response.Items = response.Items.filter(item => item.EntityName === entityFilter);
      }

      if (response.Items) {
        logger.debug(`Lookup find results: "${expression}"`, {
          count: response.Items.length,
          entities: [...new Set(response.Items.map(i => i.EntityName))]
        });
      }

      return response;
    } catch (error) {
      logger.error(`Failed to lookup find: "${expression}"`, { error });
      return {
        Items: [],
        Success: false,
        ErrorMessage: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  }

  /**
   * Search contacts with Boolean Search Tree support.
   * Uses POST /Contacts/List endpoint.
   * 
   * @param params - Search parameters including pagination and optional SearchTreeJson
   * @returns ContactSearchResponse with paginated contact results
   * 
   * @example
   * const response = await trisysAPI.searchContactsWithTree({
   *   PageNumber: 1,
   *   RecordsPerPage: 10,
   *   SearchTreeEnabled: true,
   *   SearchTreeJson: JSON.stringify(searchTree)
   * });
   */
  public async searchContactsWithTree(params: ContactSearchParams): Promise<ContactSearchResponse> {
    logger.debug('Searching contacts with tree', { 
      ...params,
      SearchTreeJson: params.SearchTreeJson ? '[SearchTree]' : undefined 
    });

    try {
      const response = await this.makeRequest<ContactSearchResponse>('/Contacts/List', params);

      if (response.Success) {
        logger.debug('Contact search completed', { 
          count: response.List?.length,
          total: response.TotalRecordCount
        });
      } else {
        logger.warn('Contact search failed', { error: response.ErrorMessage });
      }

      return response;
    } catch (error) {
      logger.error('Contact search request failed', { error });
      return {
        List: [],
        TotalRecordCount: 0,
        TotalPageCount: 0,
        FirstRowNumber: 0,
        LastRowNumber: 0,
        PageNumber: params.PageNumber,
        RecordsPerPage: params.RecordsPerPage,
        SortColumnName: null,
        SortAscending: true,
        Success: false,
        ErrorMessage: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  }

  /**
   * Convert an internal/network file path to a secure external HTTPS URL.
   * Use this when you have a file path like "G:\Images\ContactPhoto\49346\file.png"
   * and need a browser-accessible URL.
   * 
   * @param filePath - The internal file path (e.g., "G:\\Images\\ContactPhoto\\49346\\Betsy-Palmer.png")
   * @returns Object with URL on success, or error info on failure
   * 
   * @example
   * const result = await trisysAPI.convertFilePathToUrl('G:\\Images\\ContactPhoto\\49346\\Betsy-Palmer.png');
   * if (result.Success) {
   *   imageElement.src = result.URL;
   * }
   */
  public async convertFilePathToUrl(filePath: string): Promise<{ URL: string; Success: boolean; ErrorMessage: string | null }> {
    logger.debug('Converting file path to URL', { filePath });

    try {
      const response = await this.makeRequest<{ URL: string; Success: boolean; ErrorMessage: string | null }>(
        '/Files/FileReferenceInternalToExternalPath',
        { FolderFilePath: filePath }
      );

      if (response.Success) {
        logger.debug('File path converted successfully', { filePath, url: response.URL });
      } else {
        logger.warn('File path conversion failed', { filePath, error: response.ErrorMessage });
      }

      return response;
    } catch (error) {
      logger.error('File path conversion request failed', { filePath, error });
      return {
        URL: '',
        Success: false,
        ErrorMessage: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  }

  /**
   * Convert an Outlook .MSG file to HTML for viewing in a browser.
   * Returns a URL to the converted HTML file and email metadata.
   * 
   * @param filePath - The server file path to the .MSG file
   * @returns Object with HTML URL, email metadata, and success status
   * 
   * @example
   * const result = await trisysAPI.convertMSGtoHTML('G:\\Documents\\2018\\01\\31\\email.msg');
   * if (result.Success) {
   *   // result.URL contains the HTML file URL
   *   // result.EMail contains sender, recipients, subject, etc.
   * }
   */
  public async convertMSGtoHTML(filePath: string): Promise<{
    URL: string;
    EMail: {
      ToRecipients: string[];
      CcRecipients: string[];
      BccRecipients: string[];
      Subject: string;
      Attachments: string[];
      Sender: string;
      DateSent: string;
      DateRecieved: string;
    };
    Success: boolean;
    ErrorMessage: string | null;
  }> {
    logger.debug('Converting MSG file to HTML', { filePath });

    try {
      const response = await this.makeRequest<{
        URL: string;
        EMail: {
          ToRecipients: string[];
          CcRecipients: string[];
          BccRecipients: string[];
          Subject: string;
          Attachments: string[];
          Sender: string;
          DateSent: string;
          DateRecieved: string;
        };
        Success: boolean;
        ErrorMessage: string | null;
      }>('/Files/ConvertMSGtoHTML', { FilePath: filePath });

      if (response.Success) {
        logger.debug('MSG file converted successfully', { filePath, url: response.URL });
      } else {
        logger.warn('MSG file conversion failed', { filePath, error: response.ErrorMessage });
      }

      return response;
    } catch (error) {
      logger.error('MSG file conversion request failed', { filePath, error });
      return {
        URL: '',
        EMail: {
          ToRecipients: [],
          CcRecipients: [],
          BccRecipients: [],
          Subject: '',
          Attachments: [],
          Sender: '',
          DateSent: '',
          DateRecieved: ''
        },
        Success: false,
        ErrorMessage: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  }

  /**
   * Upload a file to the server's temporary storage.
   * This is step 1 of the file upload workflow.
   * After uploading, use moveUploadedFile() to move to permanent storage.
   * 
   * @param file - The File object to upload
   * @returns Array of temporary file paths on success
   * 
   * @example
   * const paths = await trisysAPI.uploadFile(photoFile);
   * if (paths.length > 0) {
   *   const moveResult = await trisysAPI.moveUploadedFile({
   *     UploadedFilePath: paths[0],
   *     TableName: 'ContactConfigFields',
   *     TableFieldName: 'ContactPhoto'
   *   });
   * }
   */
  public async uploadFile(file: File): Promise<string[]> {
    logger.debug('Uploading file', { fileName: file.name, size: file.size, type: file.type });

    try {
      const formData = new FormData();
      formData.append('trisysFile', file);

      const headers: Record<string, string> = {
        'SiteKey': getSiteKey()
      };

      // Add session key if available
      const sessionKey = this.getSessionKey();
      if (sessionKey) {
        headers['DataServicesKey'] = sessionKey;
      }

      const response = await fetch(`${API_CONFIG.baseUrl}/files/uploadfile`, {
        method: 'POST',
        headers,
        body: formData
      });

      if (!response.ok) {
        throw new Error(`Upload failed: ${response.status} ${response.statusText}`);
      }

      const paths = await response.json() as string[];
      logger.debug('File uploaded successfully', { fileName: file.name, paths });
      return paths;
    } catch (error) {
      logger.error('File upload failed', { fileName: file.name, error });
      throw error;
    }
  }

  /**
   * Move an uploaded file from temporary storage to permanent document storage.
   * This is step 2 of the file upload workflow.
   * Use the returned FolderFilePath when saving the entity.
   * 
   * @param params - Upload parameters
   * @param params.UploadedFilePath - Temporary path from uploadFile()
   * @param params.TableName - Database table name (e.g., "ContactConfigFields")
   * @param params.TableFieldName - Field name (e.g., "ContactPhoto")
   * @returns Object with permanent FolderFilePath on success
   * 
   * @example
   * const result = await trisysAPI.moveUploadedFile({
   *   UploadedFilePath: '\\Josie.Musto\\20260121_...\\photo.png',
   *   TableName: 'ContactConfigFields',
   *   TableFieldName: 'ContactPhoto'
   * });
   * if (result.Success) {
   *   // Use result.FolderFilePath in entity save
   *   fields.push({ FieldId: 429, Value: result.FolderFilePath });
   * }
   */
  public async moveUploadedFile(params: {
    UploadedFilePath: string;
    TableName: string;
    TableFieldName: string;
  }): Promise<{ FolderFilePath: string; Success: boolean; ErrorMessage: string | null }> {
    logger.debug('Moving uploaded file to permanent storage', params);

    try {
      const response = await this.makeRequest<{ FolderFilePath: string; Success: boolean; ErrorMessage: string | null }>(
        '/Files/MoveUploadedFileReferenceDocument',
        params
      );

      if (response.Success) {
        logger.debug('File moved successfully', { tempPath: params.UploadedFilePath, permanentPath: response.FolderFilePath });
      } else {
        logger.warn('File move failed', { params, error: response.ErrorMessage });
      }

      return response;
    } catch (error) {
      logger.error('File move request failed', { params, error });
      return {
        FolderFilePath: '',
        Success: false,
        ErrorMessage: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  }

  /**
   * Get the field description for a FieldId from cached standing data.
   * Useful for determining how to render a field in a CRUD form.
   * 
   * @param fieldId - The FieldId from FieldDescriptionValues
   * @returns The field description or undefined if not found
   */
  public getFieldDescription(fieldId: number): StandingDataFieldDescription | undefined {
    const standingData = this.getCachedStandingData();
    if (!standingData?.FieldDescriptions) {
      logger.warn('Standing data not loaded, cannot get field description');
      return undefined;
    }

    return standingData.FieldDescriptions.find(fd => fd.FieldId === fieldId);
  }

  // ===== Lookup Data Helpers =====

  /**
   * Get contact titles as options for select dropdowns.
   * Uses cached standing data.
   * 
   * @returns Array of {value, label} for select options
   */
  public getContactTitleOptions(): Array<{ value: string; label: string }> {
    const standingData = this.getCachedStandingData();
    
    if (!standingData?.ContactTitles) {
      logger.warn('Standing data not loaded or ContactTitles missing, using fallback titles');
      return [
        { value: '', label: 'Select...' },
        { value: 'Mr', label: 'Mr' },
        { value: 'Mrs', label: 'Mrs' },
        { value: 'Ms', label: 'Ms' },
        { value: 'Miss', label: 'Miss' },
        { value: 'Dr', label: 'Dr' }
      ];
    }

    logger.debug(`ContactTitles loaded from standing data: ${standingData.ContactTitles.length} titles`);

    const options: Array<{ value: string; label: string }> = [{ value: '', label: 'Select...' }];
    
    // Sort by Ordering and map to select options
    const sortedTitles = [...standingData.ContactTitles].sort((a, b) => a.Ordering - b.Ordering);
    for (const title of sortedTitles) {
      options.push({ value: title.Name, label: title.Name });
    }

    return options;
  }

  /**
   * Get contact priorities as options for select dropdowns.
   * Uses cached standing data.
   * 
   * @returns Array of {value, label} for select options
   */
  public getContactPriorityOptions(): Array<{ value: string; label: string }> {
    const standingData = this.getCachedStandingData();
    console.log('[DEBUG getContactPriorityOptions] ContactPriorities:', standingData?.ContactPriorities, 'type:', typeof standingData?.ContactPriorities, 'isArray:', Array.isArray(standingData?.ContactPriorities));
    if (!standingData?.ContactPriorities) {
      logger.warn('Standing data not loaded, using fallback priorities');
      return [
        { value: '', label: 'Select...' },
        { value: 'No Priority', label: 'No Priority' },
        { value: 'Worker', label: 'Worker' }
      ];
    }

    const options: Array<{ value: string; label: string }> = [{ value: '', label: 'Select...' }];
    
    // Sort by Ordering and map to select options
    console.log('[DEBUG getContactPriorityOptions] About to spread ContactPriorities');
    const sortedPriorities = [...standingData.ContactPriorities].sort((a, b) => a.Ordering - b.Ordering);
    console.log('[DEBUG getContactPriorityOptions] sortedPriorities:', sortedPriorities.length);
    for (const priority of sortedPriorities) {
      options.push({ 
        value: priority.ContactPriority, 
        label: priority.ContactPriority 
      });
    }

    return options;
  }

  /**
   * Get users as options for select dropdowns (for "Added By", "Owner" fields).
   * Uses cached standing data.
   * 
   * @returns Array of {value, label} for select options, with UserId as value
   */
  public getUserOptions(): Array<{ value: string; label: string }> {
    const standingData = this.getCachedStandingData();
    if (!standingData?.Users) {
      logger.warn('Standing data not loaded, no user options available');
      return [{ value: '', label: 'Select...' }];
    }

    const options: Array<{ value: string; label: string }> = [{ value: '', label: 'Select...' }];
    
    // Filter out locked users and sort by FullName
    const activeUsers = standingData.Users
      .filter(u => !u.Locked)
      .sort((a, b) => a.FullName.localeCompare(b.FullName));
    
    for (const user of activeUsers) {
      options.push({ 
        value: String(user.UserId), 
        label: user.FullName 
      });
    }

    return options;
  }

  /**
   * Get contact types as options for select dropdowns.
   * Uses cached standing data (ContactTypeColours).
   * 
   * @returns Array of {value, label} for select options
   */
  public getContactTypeOptions(): Array<{ value: string; label: string }> {
    const standingData = this.getCachedStandingData();
    
    // DEBUG: Log what ContactTypeColours we have
    console.log('[DEBUG getContactTypeOptions] ContactTypeColours from standing data:', standingData?.ContactTypeColours);
    
    if (!standingData?.ContactTypeColours) {
      logger.warn('Standing data not loaded, using fallback contact types');
      return [
        { value: '', label: 'Select...' },
        { value: 'Candidate', label: 'Candidate' },
        { value: 'Client', label: 'Client' },
        { value: 'ClientCandidate', label: 'Client & Candidate' },
        { value: 'User', label: 'User' }
      ];
    }

    const options: Array<{ value: string; label: string }> = [{ value: '', label: 'Select...' }];
    
    for (const type of standingData.ContactTypeColours) {
      options.push({ value: type.ContactType, label: type.ContactType });
    }
    
    console.log('[DEBUG getContactTypeOptions] Using standing data options:', options);

    return options;
  }

  /**
   * Get currencies as options for select dropdowns.
   * Uses cached standing data.
   * 
   * @returns Array of {value, label} for select options
   */
  public getCurrencyOptions(): Array<{ value: string; label: string }> {
    const standingData = this.getCachedStandingData();
    if (!standingData?.Currencies) {
      logger.warn('Standing data not loaded, using fallback currencies');
      return [
        { value: '', label: 'Select...' },
        { value: 'GBP', label: '£ GBP' },
        { value: 'EUR', label: '€ EUR' },
        { value: 'USD', label: '$ USD' }
      ];
    }

    const options: Array<{ value: string; label: string }> = [{ value: '', label: 'Select...' }];
    
    // Sort by Ordering and map to select options
    const sortedCurrencies = [...standingData.Currencies].sort((a, b) => a.Ordering - b.Ordering);
    for (const currency of sortedCurrencies) {
      options.push({ 
        value: currency.Symbol, 
        label: `${currency.Symbol} ${currency.Description}` 
      });
    }

    return options;
  }

  /**
   * Get address types as options for select dropdowns.
   * Uses cached standing data.
   * 
   * @returns Array of {value, label} for select options
   */
  public getAddressTypeOptions(): Array<{ value: number; label: string }> {
    const standingData = this.getCachedStandingData();
    if (!standingData?.AddressTypes) {
      logger.warn('Standing data not loaded, using fallback address types');
      return [
        { value: 0, label: 'Select...' },
        { value: 1, label: 'Head Office' },
        { value: 2, label: 'Branch' }
      ];
    }

    const options: Array<{ value: number; label: string }> = [{ value: 0, label: 'Select...' }];
    
    // Sort by Ordering and map to select options
    const sortedTypes = [...standingData.AddressTypes].sort((a, b) => a.Ordering - b.Ordering);
    for (const type of sortedTypes) {
      options.push({ value: type.AddressTypeId, label: type.Name });
    }

    return options;
  }

  /**
   * Get consultancy periods (rate periods) as options for select dropdowns.
   * Uses cached standing data.
   * 
   * @returns Array of {value, label} for select options
   */
  public getConsultancyPeriodOptions(): Array<{ value: number; label: string }> {
    const standingData = this.getCachedStandingData();
    if (!standingData?.ConsultancyPeriods) {
      logger.warn('Standing data not loaded, using fallback periods');
      return [
        { value: 0, label: 'Select...' },
        { value: 1, label: 'Hour' },
        { value: 2, label: 'Day' },
        { value: 3, label: 'Week' },
        { value: 4, label: 'Month' },
        { value: 5, label: 'Annum' }
      ];
    }

    const options: Array<{ value: number; label: string }> = [{ value: 0, label: 'Select...' }];
    
    for (const period of standingData.ConsultancyPeriods) {
      options.push({ value: period.ConsultancyPeriodId, label: period.Name });
    }

    return options;
  }

  /**
   * Get gender options from the Skills array, filtered by the "Gender" SkillCategory.
   * Uses cached standing data.
   * 
   * @returns Array of {value, label} for select options
   */
  public getGenderOptions(): Array<{ value: string; label: string }> {
    const standingData = this.getCachedStandingData();
    
    // Try to get gender from Skills via SkillCategories
    if (standingData?.SkillCategories && standingData?.Skills) {
      // Find the Gender skill category
      const genderCategory = standingData.SkillCategories.find(
        sc => sc.Category === 'Gender'
      );
      
      if (genderCategory) {
        // Filter skills by the Gender category
        const genderSkills = standingData.Skills.filter(
          s => s.SkillCategoryId === genderCategory.SkillCategoryId
        );
        
        if (genderSkills.length > 0) {
          // Sort by Ordering and map to options
          const options: Array<{ value: string; label: string }> = [{ value: '', label: 'Select...' }];
          const sortedSkills = [...genderSkills].sort((a, b) => a.Ordering - b.Ordering);
          
          for (const skill of sortedSkills) {
            options.push({ value: skill.Skill, label: skill.Skill });
          }
          
          return options;
        }
      }
    }
    
    // Fallback if standing data not loaded or Gender category not found
    logger.warn('Standing data not loaded or Gender category not found, using fallback genders');
    return [
      { value: '', label: 'Select...' },
      { value: 'Male', label: 'Male' },
      { value: 'Female', label: 'Female' },
      { value: 'Other', label: 'Other' },
      { value: 'Prefer not to say', label: 'Prefer not to say' }
    ];
  }

  /**
   * Get nationality/country options from the Skills array, filtered by the "Nationality" SkillCategory.
   * Uses cached standing data.
   * 
   * @returns Array of {value, label} for select options
   */
  public getNationalityOptions(): Array<{ value: string; label: string }> {
    const standingData = this.getCachedStandingData();
    
    // Try to get nationality from Skills via SkillCategories
    if (standingData?.SkillCategories && standingData?.Skills) {
      // Find the Nationality skill category
      const nationalityCategory = standingData.SkillCategories.find(
        sc => sc.Category === 'Nationality'
      );
      
      if (nationalityCategory) {
        // Filter skills by the Nationality category
        const nationalitySkills = standingData.Skills.filter(
          s => s.SkillCategoryId === nationalityCategory.SkillCategoryId
        );
        
        if (nationalitySkills.length > 0) {
          // Sort by Skill name alphabetically and map to options
          const options: Array<{ value: string; label: string }> = [{ value: '', label: 'Select...' }];
          const sortedSkills = [...nationalitySkills].sort((a, b) => a.Skill.localeCompare(b.Skill));
          
          for (const skill of sortedSkills) {
            options.push({ value: skill.Skill, label: skill.Skill });
          }
          
          logger.debug(`Nationality options loaded from standing data: ${nationalitySkills.length} nationalities`);
          return options;
        }
      }
    }
    
    // Fallback if standing data not loaded or Nationality category not found
    logger.warn('Standing data not loaded or Nationality category not found, using fallback');
    return [
      { value: '', label: 'Select...' },
      { value: 'British', label: 'British' },
      { value: 'American', label: 'American' },
      { value: 'Other', label: 'Other' }
    ];
  }

  /**
   * Get country options for address dropdowns.
   * Uses cached standing data, trying "Country" skill category first, then "Nationality".
   * 
   * @returns Array of {value, label} for select options
   */
  public getCountryOptions(): Array<{ value: string; label: string }> {
    // Try "Country" category first
    let options = this.getSkillOptionsByCategory('Country');
    if (options.length > 1) {
      return options;
    }
    
    // Fall back to "Nationality" category (countries are often stored here)
    options = this.getSkillOptionsByCategory('Nationality');
    if (options.length > 1) {
      return options;
    }
    
    // Hardcoded fallback with common countries
    logger.warn('Country/Nationality skill category not found, using fallback');
    return [
      { value: '', label: 'Select...' },
      { value: 'United Kingdom', label: 'United Kingdom' },
      { value: 'Ireland', label: 'Ireland' },
      { value: 'United States', label: 'United States' },
      { value: 'France', label: 'France' },
      { value: 'Germany', label: 'Germany' },
      { value: 'Spain', label: 'Spain' },
      { value: 'Italy', label: 'Italy' },
      { value: 'Netherlands', label: 'Netherlands' },
      { value: 'Belgium', label: 'Belgium' },
      { value: 'Australia', label: 'Australia' },
      { value: 'Canada', label: 'Canada' },
      { value: 'India', label: 'India' },
      { value: 'China', label: 'China' },
      { value: 'Japan', label: 'Japan' },
      { value: 'Other', label: 'Other' }
    ];
  }

  /**
   * Get contact status options.
   * Uses cached standing data ContactStatuses if available, otherwise returns common statuses.
   * 
   * @returns Array of {value, label} for select options
   */
  public getContactStatusOptions(): Array<{ value: string; label: string }> {
    const standingData = this.getCachedStandingData();
    
    // Check if ContactStatuses exists in standing data
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const statuses = (standingData as any)?.ContactStatuses;
    
    // DEBUG: Log what we actually have - also check Skills for Status category
    console.log('[DEBUG getContactStatusOptions] ContactStatuses from standing data:', statuses);
    
    // Also check if Status is stored in Skills like ContactSource
    const skillCategories = standingData?.SkillCategories;
    const statusCategory = skillCategories?.find((c: { Category: string }) => 
      c.Category === 'Status' || c.Category === 'ContactStatus'
    );
    console.log('[DEBUG getContactStatusOptions] Status skill category:', statusCategory);
    
    if (statusCategory && standingData?.Skills) {
      const statusSkills = standingData.Skills.filter(
        (s: { SkillCategoryId: number }) => s.SkillCategoryId === statusCategory.SkillCategoryId
      );
      console.log('[DEBUG getContactStatusOptions] Status skills from Skills table:', statusSkills);
      
      if (statusSkills.length > 0) {
        const options: Array<{ value: string; label: string }> = [{ value: '', label: 'Select...' }];
        for (const skill of statusSkills) {
          options.push({ value: skill.Skill, label: skill.Skill });
        }
        console.log('[DEBUG getContactStatusOptions] Using Skills-based status options:', options);
        return options;
      }
    }
    
    if (statuses && Array.isArray(statuses) && statuses.length > 0) {
      const options: Array<{ value: string; label: string }> = [{ value: '', label: 'Select...' }];
      for (const status of statuses) {
        options.push({ 
          value: status.Status || status.Name, 
          label: status.Status || status.Name 
        });
      }
      console.log('[DEBUG getContactStatusOptions] Using standing data options:', options);
      return options;
    }
    
    console.log('[DEBUG getContactStatusOptions] Using FALLBACK options (ContactStatuses not in standing data)');
    // Fallback - common contact statuses
    return [
      { value: '', label: 'Select...' },
      { value: 'Active', label: 'Active' },
      { value: 'Placed By Us', label: 'Placed By Us' },
      { value: 'Do Not Contact', label: 'Do Not Contact' },
      { value: 'Inactive', label: 'Inactive' },
      { value: 'Left', label: 'Left' }
    ];
  }

  /**
   * Get contact source options.
   * Uses cached standing data Skills with Category="ContactSource".
   * 
   * @returns Array of {value, label} for select options
   */
  public getContactSourceOptions(): Array<{ value: string; label: string }> {
    // Contact sources are stored in Skills table with Category = "ContactSource"
    return this.getSkillOptionsByCategory('ContactSource');
  }

  /**
   * Get skills by category name.
   * Useful for populating any skill-based dropdown from standing data.
   * 
   * @param categoryName - The SkillCategory.Category name (e.g., "Gender", "Nationality")
   * @returns Array of {value, label} for select options
   */
  public getSkillOptionsByCategory(categoryName: string): Array<{ value: string; label: string }> {
    const standingData = this.getCachedStandingData();
    
    if (!standingData?.SkillCategories || !standingData?.Skills) {
      logger.warn(`Standing data not loaded, cannot get ${categoryName} skills`);
      return [{ value: '', label: 'Select...' }];
    }
    
    // Find the skill category
    const category = standingData.SkillCategories.find(
      sc => sc.Category === categoryName
    );
    
    if (!category) {
      logger.warn(`Skill category "${categoryName}" not found`);
      return [{ value: '', label: 'Select...' }];
    }
    
    // Filter and sort skills by the category
    const categorySkills = standingData.Skills
      .filter(s => s.SkillCategoryId === category.SkillCategoryId)
      .sort((a, b) => a.Ordering - b.Ordering);
    
    const options: Array<{ value: string; label: string }> = [{ value: '', label: 'Select...' }];
    
    for (const skill of categorySkills) {
      options.push({ value: skill.Skill, label: skill.Skill });
    }
    
    return options;
  }

  /**
   * Get skills by FieldId.
   * This is the CORRECT way to populate dropdown fields - each field has a FieldId
   * and Skills with matching FieldId populate that dropdown.
   * 
   * @param fieldId - The FieldDescriptions.FieldId for the dropdown field
   * @returns Array of {value, label} for select options
   */
  public getSkillOptionsByFieldId(fieldId: number): Array<{ value: string; label: string }> {
    const standingData = this.getCachedStandingData();
    
    if (!standingData?.Skills) {
      logger.warn(`Standing data not loaded, cannot get skills for FieldId ${fieldId}`);
      return [{ value: '', label: 'Select...' }];
    }
    
    // Filter skills by FieldId and sort by Ordering
    const fieldSkills = standingData.Skills
      .filter(s => s.FieldId === fieldId)
      .sort((a, b) => a.Ordering - b.Ordering);
    
    if (fieldSkills.length === 0) {
      logger.debug(`No skills found for FieldId ${fieldId}`);
      return [{ value: '', label: 'Select...' }];
    }
    
    const options: Array<{ value: string; label: string }> = [{ value: '', label: 'Select...' }];
    
    for (const skill of fieldSkills) {
      options.push({ value: skill.Skill, label: skill.Skill });
    }
    
    logger.debug(`Got ${fieldSkills.length} skills for FieldId ${fieldId}`, { 
      firstSkill: fieldSkills[0]?.Skill 
    });
    
    return options;
  }

  // ===== Entity Field Value Helpers =====

  /**
   * Convert FieldDescriptionValues array to a keyed object.
   * Maps FieldId to the corresponding TableFieldName and value.
   * 
   * @param fieldValues - Array of FieldDescriptionValues from Entity Read
   * @param tableName - Optional table name to filter FieldDescriptions (e.g., "Contact")
   * @returns Object with TableFieldName as keys and values
   */
  public fieldValuesToObject(
    fieldValues: FieldDescriptionValue[], 
    tableName?: string
  ): Record<string, { value: string | number | boolean | null; displayValue: string | null }> {
    // Safety check: ensure fieldValues is iterable
    if (!fieldValues || !Array.isArray(fieldValues)) {
      logger.warn('fieldValuesToObject called with invalid fieldValues', { fieldValues });
      return {};
    }

    const standingData = this.getCachedStandingData();
    if (!standingData?.FieldDescriptions) {
      logger.warn('Standing data not loaded, cannot convert field values');
      return {};
    }

    const result: Record<string, { value: string | number | boolean | null; displayValue: string | null }> = {};

    // Build a map of FieldId -> TableFieldName for quick lookup
    const fieldIdToName = new Map<number, string>();
    const fieldIdToTable = new Map<number, string>();
    for (const fd of standingData.FieldDescriptions) {
      if (!tableName || fd.TableName === tableName) {
        fieldIdToName.set(fd.FieldId, fd.TableFieldName);
        fieldIdToTable.set(fd.FieldId, fd.TableName);
      }
    }

    // Track unmapped fields for debugging
    const unmappedFieldIds: number[] = [];

    // Map each field value to its TableFieldName
    for (const fv of fieldValues) {
      const fieldName = fieldIdToName.get(fv.FieldId);
      if (fieldName) {
        // Handle duplicate FieldIds (some fields appear multiple times)
        // Keep the first non-null value or the last value
        if (!result[fieldName] || (fv.Value !== null && result[fieldName].value === null)) {
          result[fieldName] = {
            value: fv.Value,
            displayValue: fv.DisplayValue
          };
        }
      } else {
        unmappedFieldIds.push(fv.FieldId);
      }
    }

    // Log mapping summary for CRUD debugging
    logger.debug('fieldValuesToObject mapping complete', {
      inputFieldCount: fieldValues.length,
      mappedFieldCount: Object.keys(result).length,
      unmappedCount: unmappedFieldIds.length,
      unmappedFieldIds: unmappedFieldIds.slice(0, 10), // First 10 only
      tableFilter: tableName || 'all tables'
    });

    return result;
  }

  /**
   * Convert a human-readable object to FieldDescriptionValues array for CREATE/UPDATE.
   * This is the reverse of fieldValuesToObject() - converts TableFieldName keys to FieldIds.
   * 
   * @param data - Object with TableFieldName keys and values
   * @param tableName - Table name to look up fields from (e.g., "Contact")
   * @returns Array of FieldDescriptionValue objects for API submission
   */
  public objectToFieldValues(
    data: Record<string, any>,
    tableName: string = 'Contact'
  ): FieldDescriptionValue[] {
    const standingData = this.getCachedStandingData();
    if (!standingData?.FieldDescriptions) {
      logger.warn('Standing data not loaded, cannot convert object to field values');
      return [];
    }

    // Build TableFieldName → FieldId map for the specified table
    // Also track FieldTypeId for debugging
    const fieldNameToId = new Map<string, number>();
    const fieldNameToTypeId = new Map<string, number>();
    const allContactFields: string[] = [];
    for (const fd of standingData.FieldDescriptions) {
      // Include both base table and ConfigFields table
      if (fd.TableName === tableName || fd.TableName === `${tableName}ConfigFields`) {
        fieldNameToId.set(fd.TableFieldName, fd.FieldId);
        fieldNameToTypeId.set(fd.TableFieldName, fd.FieldTypeId);
        allContactFields.push(fd.TableFieldName);
      }
    }
    
    // Log all available field names (helpful for debugging field name mismatches)
    console.log('[DEBUG objectToFieldValues] Available fields for', tableName, ':', 
      allContactFields.sort().join(', ')
    );

    // Convert object to FieldDescriptionValues array
    const result: FieldDescriptionValue[] = [];
    const unmappedFields: string[] = [];
    const mappedFieldsDebug: Array<{ fieldName: string; fieldId: number; fieldTypeId: number; value: string }> = [];

    for (const [fieldName, value] of Object.entries(data)) {
      // Skip null/undefined values
      if (value === undefined || value === null) continue;
      
      const fieldId = fieldNameToId.get(fieldName);
      const fieldTypeId = fieldNameToTypeId.get(fieldName) || 0;
      if (fieldId !== undefined) {
        const valueStr = String(value);
        result.push({ 
          FieldId: fieldId, 
          Value: valueStr,
          DisplayValue: null  // DisplayValue is set by the server
        });
        mappedFieldsDebug.push({ fieldName, fieldId, fieldTypeId, value: valueStr });
      } else {
        unmappedFields.push(fieldName);
      }
    }
    
    // Log ALL mapped fields for debugging lookup field issues (include FieldTypeId)
    console.log('[DEBUG objectToFieldValues] MAPPED FIELDS:', 
      mappedFieldsDebug.map(f => `${f.fieldName}(id=${f.fieldId},type=${f.fieldTypeId})="${f.value}"`).join(', ')
    );

    // Log detailed info about unmapped fields
    if (unmappedFields.length > 0) {
      console.warn('[DEBUG objectToFieldValues] UNMAPPED FIELDS:', unmappedFields);
      console.log('[DEBUG objectToFieldValues] Input data keys:', Object.keys(data));
    }

    logger.debug('objectToFieldValues mapping complete', {
      inputFieldCount: Object.keys(data).length,
      mappedFieldCount: result.length,
      unmappedCount: unmappedFields.length,
      unmappedFields: unmappedFields.slice(0, 10),
      tableName
    });

    return result;
  }

  // ===== Entity CRUD Operations =====

  /**
   * Create a new entity record.
   * 
   * @param entityName - Entity type (e.g., "Contact", "Company")
   * @param fields - FieldDescriptionValues array with field data
   * @returns Response with new record ID
   */
  public async createEntity(
    entityName: string,
    fields: FieldDescriptionValue[]
  ): Promise<{ Success: boolean; RecordId: number; ErrorMessage: string | null }> {
    const response = await this.makeRequest<{
      Operation: string;
      RecordId: number;
      EntityName: string;
      Entity: { FieldDescriptionValues: FieldDescriptionValue[] };
      Success: boolean;
      ErrorMessage: string | null;
    }>('/Entity/CreateEntity', {
      EntityName: entityName,
      EntityId: 0,  // Must be 0 for CREATE
      FieldDescriptionValues: fields
    });

    logger.info('Entity created', { 
      entityName, 
      recordId: response.RecordId,
      success: response.Success 
    });

    return {
      Success: response.Success,
      RecordId: response.RecordId,
      ErrorMessage: response.ErrorMessage
    };
  }

  /**
   * Update an existing entity record.
   * 
   * @param entityName - Entity type (e.g., "Contact", "Company")
   * @param entityId - ID of the record to update (0 or null for new records)
   * @param fields - FieldDescriptionValues array with updated field data
   * @returns Response indicating success/failure
   */
  public async updateEntity(
    entityName: string,
    entityId: number | null,
    fields: FieldDescriptionValue[]
  ): Promise<{ Success: boolean; ErrorMessage: string | null; EntityId: number | null }> {
    // CRUDOperationEnum: Create=0, Read=1, Update=2, Delete=3
    const isNewRecord = !entityId || entityId <= 0;
    const operation = isNewRecord ? 0 : 2;  // 0=Create, 2=Update
    
    logger.debug('updateEntity called', { 
      entityName, 
      entityId, 
      operation: isNewRecord ? 'Create(0)' : 'Update(2)',
      isNewRecord,
      fieldCount: fields.length
    });
    
    const response = await this.makeRequest<{
      Operation: number;
      RecordId: number;
      EntityName: string;
      Entity: { FieldDescriptionValues: FieldDescriptionValue[] };
      Success: boolean;
      ErrorMessage: string | null;
    }>('/Entity/UpdateEntity', {
      Operation: operation,
      EntityName: entityName,
      EntityId: entityId ?? 0,  // Send 0 if null
      FieldDescriptionValues: fields
    });

    logger.info('Entity updated', { 
      entityName, 
      entityId,
      operation: isNewRecord ? 'Create(0)' : 'Update(2)',
      success: response.Success 
    });

    return {
      Success: response.Success,
      ErrorMessage: response.ErrorMessage,
      EntityId: response.RecordId || null
    };
  }

  /**
   * Delete an entity record.
   * 
   * @param entityName - Entity type (e.g., "Contact", "Company")
   * @param entityId - ID of the record to delete
   * @returns Response indicating success/failure
   */
  public async deleteEntity(
    entityName: string,
    entityId: number
  ): Promise<{ Success: boolean; ErrorMessage: string | null }> {
    try {
      const response = await this.makeRequest<{
        Operation: string;
        Success: boolean;
        ErrorMessage: string | null;
      }>('/Entity/DeleteEntity', {
        EntityName: entityName,
        EntityId: entityId
      });

      logger.info('Entity deleted', { 
        entityName, 
        entityId, 
        success: response.Success 
      });

      return {
        Success: response.Success,
        ErrorMessage: response.ErrorMessage
      };
    } catch (error) {
      logger.error('Delete entity failed', { entityName, entityId, error });
      return {
        Success: false,
        ErrorMessage: error instanceof Error ? error.message : 'Delete failed'
      };
    }
  }

  // ===== Saved Search Methods =====

  /**
   * Get a list of saved searches for an entity.
   * Uses POST /EntitySearch/List - returns paginated list of saved searches.
   * 
   * @param entityName - Entity type ("Contact", "Company", "Requirement", "Placement", "Timesheet")
   * @param pageNumber - Page number (default 1)
   * @param recordsPerPage - Records per page (default 100 to get all in one request)
   * @param userNames - Optional: Filter by user names (comma-separated in single quotes, e.g., "'John Smith','Jane Doe'")
   * @returns SavedSearchListResponse with List of saved searches
   * 
   * @example
   * // Get all saved searches for Company entity
   * const response = await trisysAPI.getSavedSearches('Company');
   * for (const search of response.List) {
   *   console.log(`${search.SearchName} (${search.DefaultSearch ? 'default' : 'saved'})`);
   * }
   * 
   * @example
   * // Get saved searches filtered by user
   * const response = await trisysAPI.getSavedSearches('Contact', 1, 50, "'Josie Musto'");
   */
  public async getSavedSearches(
    entityName: string,
    pageNumber: number = 1,
    recordsPerPage: number = 100,
    userNames?: string
  ): Promise<SavedSearchListResponse> {
    logger.debug('Getting saved searches', { entityName, pageNumber, recordsPerPage, userNames });

    try {
      // If userNames not provided, get current user's display name
      let effectiveUserNames = userNames;
      if (!effectiveUserNames) {
        const profile = await this.getUserProfile();
        if (profile?.displayName) {
          // Format as single-quoted string for API: "'Josie Musto'"
          effectiveUserNames = `'${profile.displayName}'`;
          logger.debug('Using current user for saved searches', { userName: effectiveUserNames });
        }
      }

      if (!effectiveUserNames) {
        logger.warn('No user name available for saved searches');
        return {
          List: [],
          TotalRecordCount: 0,
          TotalPageCount: 0,
          FirstRowNumber: 0,
          LastRowNumber: 0,
          PageNumber: pageNumber,
          RecordsPerPage: recordsPerPage,
          SortColumnName: null,
          SortAscending: true,
          Success: false,
          ErrorMessage: 'No user name available'
        };
      }

      const body: Record<string, unknown> = {
        PageNumber: pageNumber,
        RecordsPerPage: recordsPerPage,
        EntityName: entityName,
        UserNames: effectiveUserNames
      };

      const response = await this.makeRequest<SavedSearchListResponse>('/EntitySearch/List', body);

      if (response.Success) {
        logger.debug('Saved searches retrieved', { 
          entityName, 
          count: response.List?.length ?? 0,
          totalCount: response.TotalRecordCount
        });
      } else {
        logger.warn('Failed to get saved searches', { entityName, error: response.ErrorMessage });
      }

      return response;
    } catch (error) {
      logger.error('Failed to get saved searches', { entityName, error });
      return {
        List: [],
        TotalRecordCount: 0,
        TotalPageCount: 0,
        FirstRowNumber: 0,
        LastRowNumber: 0,
        PageNumber: pageNumber,
        RecordsPerPage: recordsPerPage,
        SortColumnName: null,
        SortAscending: true,
        Success: false,
        ErrorMessage: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  }

  /**
   * Read a saved search tree by its ID.
   * Uses POST /SearchTree/Read - returns the full search criteria JSON.
   * 
   * The ControlId is constructed from the entity name and search ID:
   * `search-criteria-tree-ctrlEntitySearch-lookup-criteria-{EntityName}-id-{ApexEntitySearchId}`
   * 
   * @param entityName - Entity type ("Contact", "Company", "Requirement", etc.)
   * @param apexEntitySearchId - The ApexEntitySearchId from SavedSearchItem
   * @param userId - Optional: User ID (defaults to current user from credentials)
   * @param recordId - Optional: Record ID for record-specific searches (default 0)
   * @returns SearchTreeReadResponse with Criteria containing JsonCriteria
   * 
   * @example
   * // Read a saved search for Company
   * const response = await trisysAPI.readSavedSearch('Company', 45);
   * if (response.Success && response.Criteria) {
   *   const tree = JSON.parse(response.Criteria.JsonCriteria);
   *   booleanSearch.loadTree(tree);
   * }
   */
  public async readSavedSearch(
    entityName: string,
    apexEntitySearchId: number,
    userId?: number,
    recordId: number = 0
  ): Promise<SearchTreeReadResponse> {
    // Build the ControlId from entity name and search ID
    const controlId = `search-criteria-tree-ctrlEntitySearch-lookup-criteria-${entityName}-id-${apexEntitySearchId}`;
    
    // Get user ID from cached credentials if not provided
    let effectiveUserId = userId;
    if (!effectiveUserId) {
      const profile = await this.getUserProfile();
      effectiveUserId = profile?.userId ?? 0;
    }

    logger.debug('Reading saved search', { entityName, apexEntitySearchId, controlId, userId: effectiveUserId });

    try {
      const response = await this.makeRequest<SearchTreeReadResponse>('/SearchTree/Read', {
        UserId: effectiveUserId,
        ControlId: controlId,
        EntityName: entityName,
        RecordId: recordId,
        CloneMatchingControl: true
      });

      if (response.Success && response.Criteria) {
        logger.debug('Saved search loaded', { 
          entityName, 
          apexEntitySearchId,
          searchTreeId: response.Criteria.ApexSearchTreeId
        });
      } else {
        logger.warn('Failed to read saved search', { 
          entityName, 
          apexEntitySearchId, 
          error: response.ErrorMessage 
        });
      }

      return response;
    } catch (error) {
      logger.error('Failed to read saved search', { entityName, apexEntitySearchId, error });
      return {
        Criteria: null,
        Success: false,
        ErrorMessage: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  }

  /**
   * Delete a contact record.
   * 
   * @param contactId - ID of the contact to delete
   * @param reassignToContactId - Optional: ID of contact to reassign relationships to
   * @returns Response indicating success/failure and referential integrity errors
   */
  public async deleteContact(
    contactId: number,
    reassignToContactId: number | null = null
  ): Promise<{ Success: boolean; ReferentialIntegrityError: boolean; ErrorMessage: string | null }> {
    const response = await this.makeRequest<{
      ReferentialIntegrityError: boolean;
      Success: boolean;
      ErrorMessage: string | null;
    }>('/Contact/Delete', {
      DeleteContactId: contactId,
      ReassignToContactId: reassignToContactId
    });

    logger.info('Contact deleted', { 
      contactId, 
      success: response.Success,
      referentialIntegrityError: response.ReferentialIntegrityError
    });

    return response;
  }

  // ===== File Management =====

  /**
   * Convert an internal file path to an external UNC path for document viewing.
   * NOTE: This is for the Word Processor, not for direct file viewing.
   * For direct file viewing (images, etc.), use getFileUrl() instead.
   * 
   * @param folderFilePath - The internal file path (e.g., "G:\Formatted CVs\P\Palmer_B.docx")
   * @param preserveUNC - Whether to preserve UNC format (default: true)
   * @returns The external URL path that can be accessed for viewing
   */
  public async convertFilePathToExternal(
    folderFilePath: string,
    preserveUNC = true
  ): Promise<{ URL: string; Success: boolean; ErrorMessage: string | null }> {
    const response = await this.makeRequest<{
      URL: string;
      Success: boolean;
      ErrorMessage: string | null;
    }>('/Files/FileReferenceInternalToExternalPath', {
      FolderFilePath: folderFilePath,
      PreserveUNC: preserveUNC
    });

    logger.debug('File path converted', { 
      input: folderFilePath,
      output: response.URL,
      success: response.Success
    });

    return response;
  }

  /**
   * Get a web-accessible URL for a file.
   * This is the correct API for viewing images and other files in the browser.
   * 
   * @param filePath - The internal file path (e.g., "G:\Documents\2015\08\24\...\file.png")
   * @returns Object with FileName, URL, Success, and ErrorMessage
   */
  public async getFileUrl(
    filePath: string
  ): Promise<{ FileName: string; URL: string; Success: boolean; ErrorMessage: string | null }> {
    const response = await this.makeRequest<{
      FileName: string;
      URL: string;
      Success: boolean;
      ErrorMessage: string | null;
    }>('/Files/GetFileURL', {
      FilePath: filePath
    });

    logger.debug('File URL retrieved', { 
      input: filePath,
      fileName: response.FileName,
      url: response.URL,
      success: response.Success
    });

    return response;
  }

  /**
   * Get the document editor URL for viewing/editing a file.
   * This builds the iframe src URL for the TriSys Word Processor.
   * 
   * @param filePath - The file path to open (will be converted to external path)
   * @returns The complete document editor iframe URL, or null if conversion fails
   */
  public async getDocumentEditorUrl(filePath: string): Promise<string | null> {
    try {
      // Step 1: Convert internal path to external UNC path
      const pathResponse = await this.convertFilePathToExternal(filePath);
      if (!pathResponse.Success || !pathResponse.URL) {
        logger.error('Failed to convert file path', { filePath, error: pathResponse.ErrorMessage });
        return null;
      }

      // Step 2: Get the user identifier from stored credentials
      const credentials = this.getStoredCredentials();
      if (!credentials) {
        logger.error('No stored credentials for document editor');
        return null;
      }

      // Step 3: Build user identifier: TriSys/SignalR/Communication/{Server}/{Database}/{LoginName}
      const userPath = `TriSys/SignalR/Communication/${credentials.SQLServer}/${credentials.Database}/${credentials.LoginName}`;

      // Step 4: Generate datetime string: YYYYMMDDHHMMSS-mmm
      const now = new Date();
      const dateTime = now.getFullYear().toString() +
        String(now.getMonth() + 1).padStart(2, '0') +
        String(now.getDate()).padStart(2, '0') +
        String(now.getHours()).padStart(2, '0') +
        String(now.getMinutes()).padStart(2, '0') +
        String(now.getSeconds()).padStart(2, '0') +
        '-' + String(now.getMilliseconds()).padStart(3, '0');

      // Step 5: Base64 encode the file path and user path
      const fileBase64 = btoa(pathResponse.URL);
      const userBase64 = btoa(userPath);

      // Step 6: Determine which Apex editor to use based on file extension
      const extension = filePath.split('.').pop()?.toLowerCase() || '';
      const spreadsheetExtensions = ['xls', 'xlsx', 'csv'];
      const editorPage = spreadsheetExtensions.includes(extension) ? 'Spreadsheet.aspx' : 'WordProcessor.aspx';

      // Step 7: Build the complete URL
      const editorUrl = `${API_CONFIG.baseUrl}/apexfilemanager/${editorPage}?file=${encodeURIComponent(fileBase64)}&user=${encodeURIComponent(userBase64)}&datetime=${dateTime}`;

      logger.debug('Document editor URL built', { 
        filePath,
        editorUrl: editorUrl.substring(0, 100) + '...'
      });

      return editorUrl;
    } catch (error) {
      logger.error('Failed to build document editor URL', { filePath, error });
      return null;
    }
  }

  /**
   * Copy a formatted CV to another slot.
   * POST /ContactCV/CreateFormattedCVFileReference
   * @param originalCVPath - The source CV file path
   * @param contactId - The contact ID
   * @param targetFieldName - The target field name (e.g., 'ContactConfigFields_FormattedCVFileRef2')
   * @returns The new CV path and success status
   */
  public async copyFormattedCV(
    originalCVPath: string,
    contactId: number,
    targetFieldName: string
  ): Promise<{ FormattedCVFilePath: string; Success: boolean; ErrorMessage: string | null }> {
    try {
      const response = await this.makeRequest<{
        FormattedCVFilePath: string;
        Success: boolean;
        ErrorMessage: string | null;
      }>('/ContactCV/CreateFormattedCVFileReference', {
        OriginalCVPath: originalCVPath,
        ContactId: contactId,
        TableFieldName: targetFieldName
      });

      logger.debug('Copy formatted CV response', { response });
      return response;
    } catch (error) {
      logger.error('Failed to copy formatted CV', { originalCVPath, contactId, targetFieldName, error });
      return {
        FormattedCVFilePath: '',
        Success: false,
        ErrorMessage: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  }

  /**
   * Get stored credentials from session storage.
   * Used for building document editor URLs.
   */
  private getStoredCredentials(): { SQLServer: string; Database: string; LoginName: string } | null {
    try {
      const stored = sessionStorage.getItem(POST_LOGIN_CREDS_KEY);
      if (!stored) return null;
      
      const creds = JSON.parse(stored) as TriSysCredentialsResponse;
      if (!creds.SQLServer || !creds.Database || !creds.LoggedInUser?.LoginName) {
        return null;
      }
      
      return {
        SQLServer: creds.SQLServer,
        Database: creds.Database,
        LoginName: creds.LoggedInUser.LoginName
      };
    } catch {
      return null;
    }
  }
}

// Export singleton instance
export const trisysAPI = new TriSysWebAPIService();
